import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  assignVolunteerToEvent,
  createAdminUser,
  createCategory,
  createEvent,
  createFounderStory,
  createGroupMember,
  createMissionSupporter,
  createResource,
  createVolunteer,
  deleteCategory,
  deleteEvent,
  deleteFounderStory,
  deleteGroupMember,
  deleteMissionSupporter,
  deleteResource,
  listAdminUsers,
  listFounderStories,
  listMissionSupporters,
  listAnalyticsSummary,
  listCategories,
  listContactMessagesArchived,
  listContacts,
  listPublicContactInfo,
  listEventEmailLogs,
  listEventVolunteers,
  listEvents,
  listSubmissions,
  listResources,
  listSupportLinks,
  listVolunteersArchived,
  listVolunteers,
  resetAdminPassword,
  sendEventVolunteerEmail,
  unassignVolunteerFromEvent,
  updateContactMessage,
  updateCategory,
  updateGroupMember,
  updateMissionSupporter,
  updateOrgContactInfo,
  updateSubmissionStatus,
  updateVolunteer,
  updateResource,
  updateSupportLinks,
  updateEvent,
  updateFounderStory,
  uploadEventImage,
  uploadEventVideo,
  trackAnalyticsEvent,
} from "../services/api";

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const resourceStatusOrder = { approved: 1, pending: 2, rejected: 3 };
const eventApprovalOrder = { pending: 1, approved: 2, declined: 3 };
const buildTelHref = (phone = "") => `tel:${String(phone).replace(/[^\d+]/g, "")}`;
const buildSmsHref = (phone = "") => `sms:${String(phone).replace(/[^\d+]/g, "")}`;
const buildMapsHref = (address = "") => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(address || "").trim())}`;
const volunteerStatusOf = (volunteer) => {
  const status = String(volunteer?.volunteer_status || "").toLowerCase();
  if (status === "approved" || status === "declined" || status === "pending") {
    return status;
  }
  return volunteer?.reviewed_at ? "approved" : "pending";
};

const buildCategoryDedupIndex = (items = []) => {
  const normalizeKeyPart = (value = "") => String(value).trim().toLowerCase();
  const byKey = new Map();
  const aliasMap = new Map();
  const rootAliasMap = new Map();

  // Pass 1: dedupe major categories first.
  items.forEach((item) => {
    const parentId = Number(item?.parentId || 0);
    if (parentId) return;
    const dedupeKey = normalizeKeyPart(item?.slug || item?.name);
    const list = byKey.get(`0::${dedupeKey}`) || [];
    list.push(item);
    byKey.set(`0::${dedupeKey}`, list);
  });

  const canonicalRoots = [];
  byKey.forEach((group) => {
    const sorted = [...group].sort((a, b) => Number(a.id) - Number(b.id));
    const keeper = sorted[0];
    canonicalRoots.push({ ...keeper, parentId: null });
    sorted.forEach((row) => {
      aliasMap.set(Number(row.id), Number(keeper.id));
      rootAliasMap.set(Number(row.id), Number(keeper.id));
    });
  });

  // Pass 2: dedupe all categories using canonicalized parent IDs.
  const fullGroups = new Map();
  items.forEach((item) => {
    const originalParentId = Number(item?.parentId || 0);
    const normalizedParentId = originalParentId ? rootAliasMap.get(originalParentId) || originalParentId : 0;
    const dedupeKey = normalizeKeyPart(item?.slug || item?.name);
    const key = `${normalizedParentId}::${dedupeKey}`;
    const list = fullGroups.get(key) || [];
    list.push({ ...item, __normalizedParentId: normalizedParentId });
    fullGroups.set(key, list);
  });

  const canonical = [];
  fullGroups.forEach((group) => {
    const sorted = [...group].sort((a, b) => Number(a.id) - Number(b.id));
    const keeper = sorted[0];
    const normalizedParentId = Number(keeper.__normalizedParentId || 0);
    canonical.push({
      ...keeper,
      parentId: normalizedParentId || null,
    });
    sorted.forEach((row) => aliasMap.set(Number(row.id), Number(keeper.id)));
  });

  canonical.sort((a, b) => {
    const parentA = Number(a.parentId || 0);
    const parentB = Number(b.parentId || 0);
    if (parentA !== parentB) return parentA - parentB;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
  return { canonical, aliasMap };
};

const defaultEventForm = {
  title: "",
  startDate: "",
  endDate: "",
  isMultiDay: false,
  eventAddress: "",
  details: "",
  imageUrls: [],
  videoUrl: "",
  neededVolunteers: 0,
  isPublic: true,
  spotlight: false,
  approvalStatus: "approved",
  submitterName: "",
  submitterEmail: "",
  submitterPhone: "",
};

const defaultResourceForm = {
  name: "",
  phone: "",
  address: "",
  website: "",
  description: "",
  costScale: 0,
  status: "pending",
  zipcode: "",
  categoryIds: [],
};

const defaultOrgContactForm = {
  streetAddress: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
};

const defaultGroupMemberForm = {
  name: "",
  role: "",
  phone: "",
  email: "",
  title: "",
  displayOrder: 0,
};

const defaultFounderStoryForm = {
  storyText: "",
  founderName: "",
  founderTitle: "",
};

const defaultMissionSupporterForm = {
  supporterName: "",
  supporterSection: "heroes",
  description: "",
  isActive: true,
};

const missionSupporterSectionLabels = {
  heroes: "Heroes Helping Heroes",
  partners: "Patriot Business Partners",
};

const defaultSupportLinksForm = {
  paypalUrl: "",
  paypalDescription: "",
  venmoUrl: "",
  venmoDescription: "",
};

const getStartDateKey = (value) => {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const eventOccursOnDateKey = (event, dateKey) => {
  const startKey = getStartDateKey(event?.startDate);
  if (!startKey) return false;
  if (!event?.isMultiDay || !event?.endDate) {
    return startKey === dateKey;
  }
  const endKey = getStartDateKey(event.endDate);
  if (!endKey) return startKey === dateKey;
  return dateKey >= startKey && dateKey <= endKey;
};

const formatEventDateRange = (event) => {
  const start = new Date(event?.startDate || "");
  if (Number.isNaN(start.getTime())) return "Date not set";
  if (event?.isMultiDay && event?.endDate) {
    const end = new Date(event.endDate);
    if (!Number.isNaN(end.getTime())) {
      return `${start.toLocaleString()} - ${end.toLocaleString()}`;
    }
  }
  return start.toLocaleString();
};

const MAX_EVENT_VIDEO_SIZE = 50 * 1024 * 1024;

const getVolunteerNeedSummary = (neededVolunteers, assignedCount) => {
  const needed = Number(neededVolunteers || 0);
  const assigned = Number(assignedCount || 0);
  if (needed <= 0) return null;
  if (assigned > needed) return { text: `Overbooked by ${assigned - needed}`, bg: "danger" };
  if (assigned === needed) return { text: "Fully staffed", bg: "success" };
  return { text: `${needed - assigned} more volunteer${needed - assigned === 1 ? "" : "s"} needed`, bg: "warning" };
};

const buildCalendarDays = (events, monthYear) => {
  if (!monthYear) return [];
  const [year, month] = monthYear.split("-").map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  const firstDayOffset = start.getDay();
  const days = [];

  for (let i = 0; i < firstDayOffset; i += 1) days.push(null);
  for (let day = 1; day <= end.getDate(); day += 1) {
    const date = new Date(year, month - 1, day);
    const dateKey = date.toISOString().slice(0, 10);
    const dayEvents = events.filter((event) => eventOccursOnDateKey(event, dateKey));
    days.push({ day, events: dayEvents });
  }

  return days;
};

function AdminEventImageStrip({ images = [], title }) {
  const safeImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const firstImage = safeImages[0] || "";
  const [activeImage, setActiveImage] = useState(firstImage);

  useEffect(() => {
    setActiveImage(firstImage);
  }, [firstImage]);

  if (!safeImages.length) return null;

  return (
    <div className="d-flex flex-column gap-2 mb-2">
      <img src={activeImage} alt={title || "Event"} className="event-gallery-main" />
      {safeImages.length > 1 && (
        <div className="d-flex gap-2 flex-wrap">
          {safeImages.map((url, index) => (
            <button
              type="button"
              key={`admin-event-thumb-${title}-${index}`}
              className={`event-gallery-thumb-btn ${activeImage === url ? "is-active" : ""}`}
              onClick={() => setActiveImage(url)}
              aria-label={`Show ${title || "event"} media ${index + 1}`}
            >
              <img src={url} alt={`${title || "Event"} ${index + 1}`} className="event-gallery-thumb" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("resources");
  const [activeArchive, setActiveArchive] = useState("archive-contacts");

  // Submissions
  const [partnerSubmissions, setPartnerSubmissions] = useState([]);
  const [resources, setResources] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [volunteers, setVolunteers] = useState([]);

  // Archives
  const [archivedContacts, setArchivedContacts] = useState([]);
  const [archivedVolunteers, setArchivedVolunteers] = useState([]);

  // Events and other
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState(defaultEventForm);
  const [monthYear, setMonthYear] = useState(new Date().toISOString().slice(0, 7));
  const [message, setMessage] = useState({ type: "", text: "" });
  const [categories, setCategories] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [generatedCredential, setGeneratedCredential] = useState("");
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newSubcategoryNames, setNewSubcategoryNames] = useState({});
  const [categoryDrafts, setCategoryDrafts] = useState({});
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [orgContactForm, setOrgContactForm] = useState(defaultOrgContactForm);
  const [groupMembers, setGroupMembers] = useState([]);
  const [founderStories, setFounderStories] = useState([]);
  const [missionSupporters, setMissionSupporters] = useState([]);
  const [groupMemberForm, setGroupMemberForm] = useState(defaultGroupMemberForm);
  const [editingGroupMemberId, setEditingGroupMemberId] = useState(null);
  const [founderStoryForm, setFounderStoryForm] = useState(defaultFounderStoryForm);
  const [missionSupporterForm, setMissionSupporterForm] = useState(defaultMissionSupporterForm);
  const [editingFounderStoryId, setEditingFounderStoryId] = useState(null);
  const [editingMissionSupporterId, setEditingMissionSupporterId] = useState(null);
  const [supportLinksForm, setSupportLinksForm] = useState(defaultSupportLinksForm);

  // Modals
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [volunteerReviewForm, setVolunteerReviewForm] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [submissionEditor, setSubmissionEditor] = useState(null);
  const [isCreatingResource, setIsCreatingResource] = useState(false);
  const [adminCalendarModalEvents, setAdminCalendarModalEvents] = useState([]);
  const [adminCalendarModalDate, setAdminCalendarModalDate] = useState("");
  const [showCategoryManagerModal, setShowCategoryManagerModal] = useState(false);
  const [analyticsMetric, setAnalyticsMetric] = useState("visits_by_page");
  const [analyticsRange, setAnalyticsRange] = useState("30d");
  const [analyticsFrom, setAnalyticsFrom] = useState("");
  const [analyticsTo, setAnalyticsTo] = useState("");
  const [analyticsTitle, setAnalyticsTitle] = useState("");
  const [analyticsRows, setAnalyticsRows] = useState([]);
  const [analyticsTotals, setAnalyticsTotals] = useState({ events: 0 });
  const [analyticsWindow, setAnalyticsWindow] = useState({ startAt: "", endAt: "" });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Volunteer assignment
  const [volAssignmentForm, setVolAssignmentForm] = useState({ eventId: "", volunteerId: "", assignedTime: "", role: "", bringItems: "" });
  const [coordinationEventId, setCoordinationEventId] = useState("");
  const [eventVolunteers, setEventVolunteers] = useState([]);
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [eventEmailLogs, setEventEmailLogs] = useState([]);
  const [emailBlastForm, setEmailBlastForm] = useState({ subject: "", message: "" });
  const [newVolunteerForm, setNewVolunteerForm] = useState({
    name: "",
    email: "",
    phone: "",
    interests: "",
    availability: "",
    eventInterestType: "any",
    preferredEventId: "",
    message: "",
    approveNow: true,
  });

  // Event management
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventFormBeforeEdit, setEventFormBeforeEdit] = useState(null);

  const extractGeneratedPassword = (response) =>
    String(response?.data?.generatedPassword || response?.generatedPassword || "").trim();

  const copyCredentialText = async () => {
    if (!generatedCredential) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(generatedCredential);
      } else {
        const input = document.createElement("textarea");
        input.value = generatedCredential;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setMessage({ type: "success", text: "Credential copied to clipboard." });
    } catch {
      window.prompt("Copy this credential:", generatedCredential);
    }
  };

  const calendarDays = useMemo(() => buildCalendarDays(events, monthYear), [events, monthYear]);
  const pendingSubmissions = useMemo(
    () => partnerSubmissions.filter((submission) => submission.status === "pending"),
    [partnerSubmissions],
  );
  const pendingContactMessages = useMemo(
    () => contactMessages.filter((msg) => String(msg.review_status || msg.reviewStatus || "pending") === "pending"),
    [contactMessages],
  );
  const pendingVolunteers = useMemo(
    () => volunteers.filter((vol) => volunteerStatusOf(vol) === "pending"),
    [volunteers],
  );
  const sortedResources = useMemo(
    () =>
      [...resources].sort((a, b) => {
        const left = resourceStatusOrder[a.status] || 99;
        const right = resourceStatusOrder[b.status] || 99;
        if (left !== right) return left - right;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
    [resources],
  );
  const submittedEvents = useMemo(
    () =>
      [...events]
        .filter((event) => String(event.submissionSource || "admin") === "public")
        .sort((a, b) => {
          const statusDiff = (eventApprovalOrder[a.approvalStatus] || 99) - (eventApprovalOrder[b.approvalStatus] || 99);
          if (statusDiff !== 0) return statusDiff;
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        }),
    [events],
  );
  const pendingSubmittedEvents = useMemo(
    () => submittedEvents.filter((event) => String(event.approvalStatus || "pending") === "pending"),
    [submittedEvents],
  );
  const selectedCoordinationEvent = useMemo(
    () => events.find((evt) => String(evt.id) === String(coordinationEventId)) || null,
    [events, coordinationEventId],
  );
  const selectedNeededVolunteers = Number(selectedCoordinationEvent?.neededVolunteers || 0);
  const assignedVolunteerCount = eventVolunteers.length;
  const remainingVolunteerSlots = Math.max(0, selectedNeededVolunteers - assignedVolunteerCount);
  const overAssignedCount = Math.max(0, assignedVolunteerCount - selectedNeededVolunteers);
  const assignmentEventId = Number(volAssignmentForm.eventId || coordinationEventId || 0);
  const assignedVolunteerIds = useMemo(
    () => new Set(eventVolunteers.map((item) => Number(item.volunteer_id))),
    [eventVolunteers],
  );
  const preferredMatchCount = useMemo(
    () =>
      archivedVolunteers.filter((vol) => {
        const preferredEventId = Number(vol.preferred_event_id || vol.preferredEventId || 0);
        const interestType = String(vol.event_interest_type || vol.eventInterestType || "");
        return preferredEventId === assignmentEventId && interestType !== "any";
      }).length,
    [archivedVolunteers, assignmentEventId],
  );
  const assignableVolunteers = useMemo(
    () =>
      archivedVolunteers
        .filter((vol) => volunteerStatusOf(vol) === "approved")
        .filter((vol) => !assignedVolunteerIds.has(Number(vol.id)))
        .filter((vol) => {
          if (assignmentFilter !== "preferred" || !assignmentEventId) {
            return true;
          }
          const preferredEventId = Number(vol.preferred_event_id || vol.preferredEventId || 0);
          const interestType = String(vol.event_interest_type || vol.eventInterestType || "");
          return preferredEventId === assignmentEventId && interestType !== "any";
        })
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [archivedVolunteers, assignedVolunteerIds, assignmentFilter, assignmentEventId],
  );
  const approvedVolunteers = useMemo(
    () => archivedVolunteers.filter((vol) => volunteerStatusOf(vol) === "approved"),
    [archivedVolunteers],
  );
  const { canonical: dedupedCategories, aliasMap: categoryIdAliasMap } = useMemo(
    () => buildCategoryDedupIndex(categories),
    [categories],
  );
  const normalizeCategoryIds = (ids = []) =>
    Array.from(
      new Set(
        (ids || [])
          .map((id) => Number(id))
          .filter((id) => id > 0)
          .map((id) => categoryIdAliasMap.get(id) || id),
      ),
    );
  const majorCategories = useMemo(
    () => dedupedCategories.filter((category) => !Number(category.parentId || 0)),
    [dedupedCategories],
  );
  const childCategoriesByParent = useMemo(() => {
    const map = new Map();
    dedupedCategories.forEach((category) => {
      const parentId = Number(category.parentId || 0);
      if (!parentId) return;
      const existing = map.get(parentId) || [];
      existing.push(category);
      map.set(parentId, existing);
    });
    map.forEach((list) => list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
    return map;
  }, [dedupedCategories]);
  const categoryMap = useMemo(
    () => new Map(dedupedCategories.map((category) => [Number(category.id), category])),
    [dedupedCategories],
  );

  const loadData = async () => {
    try {
      const [partnerResponse, resourcesResponse, contactResponse, volunteerResponse, contactArchiveResponse, volunteerArchiveResponse, eventsResponse, categoriesResponse, adminUsersResponse, publicContactInfoResponse, founderStoriesResponse, supportLinksResponse, missionSupportersResponse] = await Promise.all([
        listSubmissions(),
        listResources(),
        listContacts(),
        listVolunteers(),
        listContactMessagesArchived(),
        listVolunteersArchived(),
        listEvents({ includeAll: true }),
        listCategories(),
        listAdminUsers(),
        listPublicContactInfo(),
        listFounderStories().catch(() => ({ data: [] })),
        listSupportLinks().catch(() => ({ data: [] })),
        listMissionSupporters({ includeInactive: true }).catch(() => ({ data: [] })),
      ]);

      setPartnerSubmissions(partnerResponse.data || []);
      setResources(resourcesResponse.data || []);
      setContactMessages(contactResponse.data || []);
      setVolunteers(volunteerResponse.data || []);
      setArchivedContacts(contactArchiveResponse.data || []);
      setArchivedVolunteers(volunteerArchiveResponse.data || []);
      setEvents(eventsResponse.data || []);
      setCategories(categoriesResponse.data || []);
      setAdminUsers(adminUsersResponse.data || []);
      setOrgContactForm({
        streetAddress: publicContactInfoResponse.data?.organization?.streetAddress || "",
        city: publicContactInfoResponse.data?.organization?.city || "",
        state: publicContactInfoResponse.data?.organization?.state || "",
        zip: publicContactInfoResponse.data?.organization?.zip || "",
        phone: publicContactInfoResponse.data?.organization?.phone || "",
        email: publicContactInfoResponse.data?.organization?.email || "",
      });
      setGroupMembers(
        [...(publicContactInfoResponse.data?.members || [])].sort(
          (a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0),
        ),
      );
      setFounderStories(founderStoriesResponse.data || []);
      setMissionSupporters(missionSupportersResponse.data || []);
      const supportByProvider = new Map((supportLinksResponse.data || []).map((item) => [String(item.provider || "").toLowerCase(), item]));
      setSupportLinksForm({
        paypalUrl: supportByProvider.get("paypal")?.url || "",
        paypalDescription: supportByProvider.get("paypal")?.description || "",
        venmoUrl: supportByProvider.get("venmo")?.url || "",
        venmoDescription: supportByProvider.get("venmo")?.description || "",
      });
    } catch (error) {
      if (error?.status === 401) {
        localStorage.removeItem("fvg-admin-auth");
        navigate("/admin/login");
        return;
      }
      setMessage({ type: "danger", text: error.message || "Failed to load admin data." });
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await listAnalyticsSummary({
        metric: analyticsMetric,
        range: analyticsRange,
        from: analyticsRange === "custom" ? analyticsFrom : "",
        to: analyticsRange === "custom" ? analyticsTo : "",
      });
      const payload = response.data || {};
      setAnalyticsTitle(payload.title || "");
      setAnalyticsRows(payload.rows || []);
      setAnalyticsTotals(payload.totals || { events: 0 });
      setAnalyticsWindow({
        startAt: payload.startAt || "",
        endAt: payload.endAt || "",
      });
    } catch (error) {
      if (error?.status === 401) {
        localStorage.removeItem("fvg-admin-auth");
        navigate("/admin/login");
        return;
      }
      setMessage({ type: "danger", text: error.message || "Failed to load analytics." });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const trackAdminActivity = (eventName, payload = {}) => {
    const adminEmail = localStorage.getItem("fvg-admin-email") || "";
    trackAnalyticsEvent({
      eventType: "admin_activity",
      eventName,
      pagePath: "/admin",
      adminEmail,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      payload,
    }).catch(() => {});
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (analyticsRange === "custom" && (!analyticsFrom || !analyticsTo)) return;
    loadAnalytics();
  }, [analyticsMetric, analyticsRange, analyticsFrom, analyticsTo]);

  useEffect(() => {
    trackAdminActivity("admin_page_visit", {
      tab: activeTab,
      archiveTab: activeArchive,
    });
  }, [activeTab, activeArchive]);

  useEffect(() => {
    setCategoryDrafts((prev) => {
      const next = { ...prev };
      dedupedCategories.forEach((category) => {
        if (!next[category.id]) {
          next[category.id] = category.name;
        }
      });
      return next;
    });
  }, [dedupedCategories]);

  useEffect(() => {
    if (coordinationEventId) {
      loadEventVolunteers(coordinationEventId);
      loadEventEmailLogs(coordinationEventId);
    }
  }, [coordinationEventId]);

  const openSubmissionEditor = (submission) => {
    const services = Array.isArray(submission.services) ? submission.services : [];
    const subcategory = Array.isArray(submission.subcategory) ? submission.subcategory : [];
    const customSingle = submission.customSingle || null;
    const customList = submission.customList || null;
    setSubmissionEditor({
      id: submission.id,
      status: submission.status || "pending",
      createdAt: submission.createdAt || "",
      organizationName: submission.organizationName || "",
      contactName: submission.contactName || "",
      email: submission.email || "",
      phone: submission.phone || "",
      notes: submission.notes || "",
      proposedCategory: "",
      name: submission.name || submission.organizationName || "",
      resourcePhone: submission.resourcePhone || submission.phone || "",
      address: submission.address || "",
      website: submission.website || "",
      costScale: Number(submission.costScale || 0),
      zipcode: submission.zipcode || "",
      description: submission.description || "",
      categoryIds: normalizeCategoryIds(submission.categoryIds || []),
      categoryNames: Array.isArray(submission.categoryNames) ? submission.categoryNames : [],
      contactPerson: submission.contactPerson || "",
      services,
      subcategory,
      customSingle,
      customList,
    });
  };

  const handleSubmissionEditorChange = (event) => {
    const { name, value } = event.target;
    setSubmissionEditor((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCategoryIdsWithHierarchy = (selectedIds = [], categoryId) => {
    const targetId = Number(categoryId);
    const next = new Set(normalizeCategoryIds(selectedIds || []));
    const category = categoryMap.get(targetId);
    if (!category) return Array.from(next);

    if (next.has(targetId)) {
      next.delete(targetId);
      const childList = childCategoriesByParent.get(targetId) || [];
      childList.forEach((child) => next.delete(Number(child.id)));
      return normalizeCategoryIds(Array.from(next));
    }

    next.add(targetId);
    const parentId = Number(category.parentId || 0);
    if (parentId) {
      next.add(parentId);
    }
    return normalizeCategoryIds(Array.from(next));
  };

  const toggleEditorCategory = (categoryId) => {
    setSubmissionEditor((prev) => {
      const selected = prev.categoryIds || [];
      const next = toggleCategoryIdsWithHierarchy(selected, categoryId);
      return { ...prev, categoryIds: next };
    });
  };

  const saveSubmissionEditor = async (statusOverride = null) => {
    if (!submissionEditor) return;

    try {
      const nextStatus = statusOverride || submissionEditor.status || "pending";
      await updateSubmissionStatus({
        id: submissionEditor.id,
        status: nextStatus,
        organizationName: submissionEditor.organizationName || "",
        contactName: submissionEditor.contactName || "",
        email: submissionEditor.email || "",
        phone: submissionEditor.phone || "",
        notes: submissionEditor.notes || "",
        name: submissionEditor.name || "",
        resourcePhone: submissionEditor.resourcePhone || "",
        address: submissionEditor.address || "",
        website: submissionEditor.website || "",
        costScale: Number(submissionEditor.costScale || 0),
        zipcode: submissionEditor.zipcode || "",
        description: submissionEditor.description || "",
        contactPerson: submissionEditor.contactPerson || "",
        services: Array.isArray(submissionEditor.services) ? submissionEditor.services : [],
        subcategory: Array.isArray(submissionEditor.subcategory) ? submissionEditor.subcategory : [],
        customSingle: submissionEditor.customSingle || null,
        customList: submissionEditor.customList || null,
        proposedCategory: "",
        categoryIds: normalizeCategoryIds(submissionEditor.categoryIds || []),
      });
      trackAdminActivity("resource_submission_review_saved", {
        submissionId: submissionEditor.id,
        decision: nextStatus,
      });
      setSubmissionEditor(null);
      await loadData();
      loadAnalytics();
      setMessage({ type: "success", text: `Submission ${nextStatus}.` });
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to save submission." });
    }
  };

  const handleContactUpdate = async (id, reviewStatus, adminNotes) => {
    try {
      await updateContactMessage({ id, reviewStatus, adminNotes });
      setMessage({ type: "success", text: "Contact message updated." });
      setEditingSubmission(null);
      await loadData();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update contact message." });
    }
  };

  const handleVolunteerUpdate = async (payload) => {
    try {
      await updateVolunteer(payload);
      setMessage({ type: "success", text: "Volunteer updated." });
      setEditingSubmission(null);
      setVolunteerReviewForm(null);
      await loadData();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update volunteer." });
    }
  };

  const openVolunteerReview = (volunteer) => {
    setEditingSubmission(volunteer);
    setEditingType("volunteer");
    setVolunteerReviewForm({
      id: volunteer.id,
      name: volunteer.name || "",
      email: volunteer.email || "",
      phone: volunteer.phone || "",
      interests: volunteer.interests || "",
      availability: volunteer.availability || "",
      eventInterestType: volunteer.event_interest_type || volunteer.eventInterestType || "any",
      preferredEventId: volunteer.preferred_event_id || volunteer.preferredEventId || "",
      message: volunteer.message || "",
      adminNotes: volunteer.admin_notes || "",
      volunteerStatus: volunteerStatusOf(volunteer),
    });
  };

  const handleEditResource = (res) => {
    setIsCreatingResource(false);
    setEditingResource({
      ...res,
      categoryIds: normalizeCategoryIds(res.categoryIds || []),
    });
  };

  const handleCreateResource = () => {
    setIsCreatingResource(true);
    setEditingResource({ ...defaultResourceForm });
  };

  const handleResourceChange = (e) => {
    const { name, value } = e.target;
    setEditingResource((prev) => ({ ...prev, [name]: value }));
  };

  const saveResourceChanges = async () => {
    if (!editingResource) return;
    try {
      const payload = {
        ...editingResource,
        categoryIds: normalizeCategoryIds(editingResource.categoryIds || []),
      };
      const resp = isCreatingResource ? await createResource(payload) : await updateResource(payload);
      if (resp.success) {
        setMessage({ type: "success", text: isCreatingResource ? "Resource created." : "Resource updated." });
        setEditingResource(null);
        setIsCreatingResource(false);
        await loadData();
      } else {
        setMessage({ type: "danger", text: resp.message || "Failed to save resource." });
      }
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Error saving resource." });
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm("Delete this resource? This cannot be undone.")) return;
    try {
      await deleteResource(resourceId);
      setMessage({ type: "success", text: "Resource deleted." });
      await loadData();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to delete resource." });
    }
  };

  const handleCreateCategory = async (parentId = null) => {
    const isSubcategory = Number(parentId || 0) > 0;
    const rawName = isSubcategory ? (newSubcategoryNames[parentId] || "") : newCategoryName;
    const name = rawName.trim();
    if (!name) return;
    try {
      const response = await createCategory({ name, parentId: isSubcategory ? Number(parentId) : null });
      const createdId = Number(response?.data?.id || 0);
      if (isSubcategory) {
        setNewSubcategoryNames((prev) => ({ ...prev, [parentId]: "" }));
      } else {
        setNewCategoryName("");
      }
      await loadData();
      if (createdId && editingResource) {
        setEditingResource((prev) => {
          const current = (prev?.categoryIds || []).map(Number);
          const nextSet = new Set(current);
          nextSet.add(createdId);
          const created = response?.data || {};
          const createdParentId = Number(created.parentId || 0);
          if (createdParentId) {
            nextSet.add(createdParentId);
          }
          return { ...prev, categoryIds: normalizeCategoryIds(Array.from(nextSet)) };
        });
      }
      if (createdId && submissionEditor) {
        setSubmissionEditor((prev) => {
          const current = (prev?.categoryIds || []).map(Number);
          const nextSet = new Set(current);
          nextSet.add(createdId);
          const created = response?.data || {};
          const createdParentId = Number(created.parentId || 0);
          if (createdParentId) {
            nextSet.add(createdParentId);
          }
          return { ...prev, categoryIds: normalizeCategoryIds(Array.from(nextSet)) };
        });
      }
      setMessage({ type: "success", text: isSubcategory ? "Subcategory added." : "Category added." });
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to add category." });
    }
  };

  const handleRenameCategory = async (categoryId) => {
    const nextName = (categoryDrafts[categoryId] || "").trim();
    if (!nextName) return;
    try {
      const existing = categoryMap.get(Number(categoryId));
      await updateCategory({
        id: categoryId,
        name: nextName,
        parentId: existing ? Number(existing.parentId || 0) || null : null,
      });
      await loadData();
      setEditingCategoryId(null);
      setMessage({ type: "success", text: "Category saved." });
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to save category." });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Remove this category from active use?")) return;
    try {
      const affectedIds = new Set([Number(categoryId)]);
      categories.forEach((category) => {
        if (Number(category.parentId || 0) === Number(categoryId)) {
          affectedIds.add(Number(category.id));
        }
      });

      await deleteCategory(categoryId);
      if (Number(editingCategoryId) === Number(categoryId)) {
        setEditingCategoryId(null);
      }
      setEditingResource((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categoryIds: normalizeCategoryIds((prev.categoryIds || []).filter((id) => !affectedIds.has(Number(id)))),
        };
      });
      setSubmissionEditor((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categoryIds: normalizeCategoryIds((prev.categoryIds || []).filter((id) => !affectedIds.has(Number(id)))),
        };
      });
      await loadData();
      setMessage({ type: "success", text: "Category removed." });
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to remove category." });
    }
  };

  const handleVolunteerAssignment = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    if (!volAssignmentForm.eventId || !volAssignmentForm.volunteerId) {
      setMessage({ type: "danger", text: "Please select both an event and a volunteer." });
      return;
    }
    try {
      await assignVolunteerToEvent({
        eventId: parseInt(volAssignmentForm.eventId),
        volunteerId: parseInt(volAssignmentForm.volunteerId),
        assignedTime: volAssignmentForm.assignedTime || null,
        role: volAssignmentForm.role || null,
        bringItems: volAssignmentForm.bringItems || null,
      });
      setVolAssignmentForm({ eventId: coordinationEventId || "", volunteerId: "", assignedTime: "", role: "", bringItems: "" });
      setMessage({ type: "success", text: "Volunteer assigned to event." });
      await loadData();
      await loadEventVolunteers(volAssignmentForm.eventId);
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to assign volunteer." });
    }
  };

  const handleEventCreate = async (event) => {
    event.preventDefault();
    setMessage({ type: "", text: "" });
    try {
      await createEvent(eventForm);
      trackAdminActivity("event_created", { title: eventForm.title || "" });
      setEventForm(defaultEventForm);
      await loadData();
      loadAnalytics();
      setMessage({ type: "success", text: "Event saved." });
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to create event." });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setMessage({ type: "info", text: "Uploading event media..." });
      const result = await uploadEventImage(file);

      if (result.success) {
        setEventForm((prev) => {
          const existing = prev.imageUrls || [];
          if (existing.includes(result.data.url)) return prev;
          return { ...prev, imageUrls: [...existing, result.data.url] };
        });
        setMessage({ type: "success", text: "Event media uploaded successfully!" });
      } else {
        setMessage({ type: "danger", text: result.message || "Failed to upload event media." });
      }
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Error uploading event media." });
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setMessage({ type: "danger", text: "Only video files are allowed." });
      return;
    }
    if (file.size > MAX_EVENT_VIDEO_SIZE) {
      setMessage({ type: "danger", text: "Video is too large (max 50MB). Reduce file size to continue." });
      return;
    }

    try {
      setMessage({ type: "info", text: "Uploading video..." });
      const result = await uploadEventVideo(file);
      if (result.success) {
        setEventForm((prev) => ({ ...prev, videoUrl: result.data.url }));
        setMessage({ type: "success", text: "Video uploaded successfully!" });
      } else {
        setMessage({ type: "danger", text: result.message || "Failed to upload video." });
      }
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Error uploading video." });
    }
  };

  const handleEditEvent = (eventData) => {
    setEventFormBeforeEdit(eventForm);
    setEditingEvent(eventData);
    setEventForm({
      title: eventData.title,
      startDate: eventData.startDate,
      endDate: eventData.endDate || "",
      isMultiDay: Boolean(eventData.isMultiDay),
      eventAddress: eventData.eventAddress || "",
      details: eventData.details,
      imageUrls: eventData.imageUrls?.length ? eventData.imageUrls : eventData.imageUrl ? [eventData.imageUrl] : [],
      videoUrl: eventData.videoUrl || "",
      neededVolunteers: eventData.neededVolunteers || 0,
      isPublic: eventData.isPublic,
      spotlight: Boolean(eventData.spotlight),
      approvalStatus: eventData.approvalStatus || "approved",
      submitterName: eventData.submitterName || "",
      submitterEmail: eventData.submitterEmail || "",
      submitterPhone: eventData.submitterPhone || "",
    });
    setShowEventModal(true);
  };

  const closeEditEventModal = (restoreDraft = true) => {
    setShowEventModal(false);
    setEditingEvent(null);
    setEventForm((restoreDraft && eventFormBeforeEdit) ? eventFormBeforeEdit : defaultEventForm);
    setEventFormBeforeEdit(null);
  };

  const handleSaveEditedEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent?.id) return;

    try {
      const response = await updateEvent({
        id: editingEvent.id,
        ...eventForm,
      });
      if (response?.success === false) {
        throw new Error(response.message || "Failed to update event.");
      }
      trackAdminActivity("event_updated", { eventId: editingEvent.id });

      setMessage({ type: "success", text: "Event updated." });
      closeEditEventModal(false);
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update event." });
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await deleteEvent(eventId);
      trackAdminActivity("event_deleted", { eventId });

      setMessage({ type: "success", text: "Event deleted." });
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to delete event." });
    }
  };

  const handleEventApprovalUpdate = async (eventData, approvalStatus) => {
    if (!eventData?.id) return;
    try {
      await updateEvent({
        id: eventData.id,
        title: eventData.title,
        startDate: eventData.startDate,
        endDate: eventData.endDate || "",
        isMultiDay: Boolean(eventData.isMultiDay),
        eventAddress: eventData.eventAddress || "",
        details: eventData.details || "",
        imageUrls: eventData.imageUrls?.length ? eventData.imageUrls : eventData.imageUrl ? [eventData.imageUrl] : [],
        neededVolunteers: Number(eventData.neededVolunteers || 0),
        videoUrl: eventData.videoUrl || "",
        isPublic: approvalStatus === "approved",
        spotlight: Boolean(eventData.spotlight),
        approvalStatus,
        submitterName: eventData.submitterName || "",
        submitterEmail: eventData.submitterEmail || "",
        submitterPhone: eventData.submitterPhone || "",
      });
      trackAdminActivity("event_suggestion_decision", {
        eventId: eventData.id,
        approvalStatus,
      });
      setMessage({ type: "success", text: `Event marked ${approvalStatus}.` });
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update event approval status." });
    }
  };

  const removeEventImageAt = (indexToRemove) => {
    setEventForm((prev) => ({
      ...prev,
      imageUrls: (prev.imageUrls || []).filter((_, index) => index !== indexToRemove),
    }));
  };

  const loadEventVolunteers = async (eventId) => {
    if (!eventId) {
      setEventVolunteers([]);
      return;
    }
    try {
      const response = await listEventVolunteers(eventId);
      setEventVolunteers(response.data || []);
    } catch {
      setEventVolunteers([]);
    }
  };

  const loadEventEmailLogs = async (eventId) => {
    if (!eventId) {
      setEventEmailLogs([]);
      return;
    }
    try {
      const response = await listEventEmailLogs(eventId);
      setEventEmailLogs(response.data || []);
    } catch {
      setEventEmailLogs([]);
    }
  };

  const handleCoordinationEventChange = async (eventId) => {
    setCoordinationEventId(eventId);
    setVolAssignmentForm((prev) => ({ ...prev, eventId, volunteerId: "" }));
    setAssignmentFilter("all");
    const target = events.find((evt) => String(evt.id) === String(eventId));
    if (target) {
      const eventDate = target.startDate ? new Date(target.startDate).toLocaleDateString() : "";
      const eventLink = `${window.location.origin}/events/${target.id}`;
      setEmailBlastForm({
        subject: `${target.title}${eventDate ? ` - ${eventDate}` : ""}`,
        message: `Event update for ${target.title}${eventDate ? ` on ${eventDate}` : ""}.\n\nEvent page: ${eventLink}\n\n`,
      });
    } else {
      setEmailBlastForm({ subject: "", message: "" });
    }
    await loadEventVolunteers(eventId);
    await loadEventEmailLogs(eventId);
  };

  const handleUnassignVolunteer = async (volunteerId) => {
    const targetEventId = Number(coordinationEventId || volAssignmentForm.eventId || 0);
    if (!targetEventId || !volunteerId) return;
    try {
      await unassignVolunteerFromEvent({ eventId: targetEventId, volunteerId: Number(volunteerId) });
      setVolAssignmentForm((prev) => ({ ...prev, volunteerId: "" }));
      setMessage({ type: "success", text: "Volunteer unassigned from event." });
      await loadData();
      await loadEventVolunteers(targetEventId);
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to unassign volunteer." });
    }
  };

  const handleSaveNeededSlots = async () => {
    const targetId = Number(coordinationEventId);
    if (!targetId) return;
    const targetEvent = events.find((evt) => Number(evt.id) === targetId);
    if (!targetEvent) return;
    try {
      await updateEvent({
        id: targetEvent.id,
        title: targetEvent.title,
        startDate: targetEvent.startDate,
        endDate: targetEvent.endDate || "",
        isMultiDay: Boolean(targetEvent.isMultiDay),
        eventAddress: targetEvent.eventAddress || "",
        details: targetEvent.details,
        imageUrls: targetEvent.imageUrls?.length ? targetEvent.imageUrls : targetEvent.imageUrl ? [targetEvent.imageUrl] : [],
        videoUrl: targetEvent.videoUrl || "",
        neededVolunteers: targetEvent.neededVolunteers || 0,
        isPublic: targetEvent.isPublic,
        spotlight: Boolean(targetEvent.spotlight),
        approvalStatus: targetEvent.approvalStatus || "approved",
        submitterName: targetEvent.submitterName || "",
        submitterEmail: targetEvent.submitterEmail || "",
        submitterPhone: targetEvent.submitterPhone || "",
      });
      setMessage({ type: "success", text: "Needed volunteer slots updated." });
      await loadData();
      await loadEventVolunteers(targetId);
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update slots." });
    }
  };

  const handleUpdateAssignedVolunteerDetails = async (item) => {
    const targetEventId = Number(coordinationEventId || 0);
    if (!targetEventId || !item?.volunteer_id) return;
    try {
      await assignVolunteerToEvent({
        eventId: targetEventId,
        volunteerId: Number(item.volunteer_id),
        assignedTime: item.assigned_time || null,
        role: item.role || null,
        bringItems: item.bring_items || null,
      });
      setMessage({ type: "success", text: `Updated assignment for ${item.name}.` });
      await loadData();
      await loadEventVolunteers(targetEventId);
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update volunteer assignment details." });
    }
  };

  const handleEditEventVolunteerField = (volunteerId, field, value) => {
    setEventVolunteers((prev) =>
      prev.map((item) =>
        Number(item.volunteer_id) === Number(volunteerId)
          ? { ...item, [field]: value }
          : item,
      ),
    );
  };

  const handleExportEventVolunteerCsv = () => {
    const selectedEvent = events.find((evt) => String(evt.id) === String(coordinationEventId));
    if (!selectedEvent || eventVolunteers.length === 0) return;
    const escapeCsv = (value) => {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const rows = [
      ["Event", "Volunteer", "Email", "Phone", "Time", "Job", "What To Bring"],
      ...eventVolunteers.map((item) => [
        selectedEvent.title || "",
        item.name || "",
        item.email || "",
        item.phone || "",
        item.assigned_time || "",
        item.role || "",
        item.bring_items || "",
      ]),
    ];
    const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `event-volunteers-${selectedEvent.id}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleSendEventVolunteerBlast = async (e) => {
    e.preventDefault();
    const targetEventId = Number(coordinationEventId || 0);
    if (!targetEventId) {
      setMessage({ type: "danger", text: "Select an event first." });
      return;
    }
    if (!emailBlastForm.subject.trim() || !emailBlastForm.message.trim()) {
      setMessage({ type: "danger", text: "Subject and message are required." });
      return;
    }
    try {
      const response = await sendEventVolunteerEmail({
        eventId: targetEventId,
        subject: emailBlastForm.subject.trim(),
        message: emailBlastForm.message.trim(),
      });
      const details = response?.data || {};
      setMessage({
        type: "success",
        text: `Blast sent: ${details.sentCount ?? 0} sent, ${details.failedCount ?? 0} failed.`,
      });
      setEmailBlastForm((prev) => ({ ...prev, message: "" }));
      await loadData();
      await loadEventEmailLogs(targetEventId);
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to send volunteer blast." });
    }
  };

  const openAdminCalendarDay = (day) => {
    if (!day?.events?.length) return;
    const [year, month] = monthYear.split("-").map(Number);
    const date = new Date(year, month - 1, day.day);
    setAdminCalendarModalDate(date.toISOString().slice(0, 10));
    setAdminCalendarModalEvents(day.events);
  };

  const handleNewVolunteerChange = (event) => {
    const { name, value, type, checked } = event.target;
    setNewVolunteerForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateVolunteer = async (event) => {
    event.preventDefault();
    if (!newVolunteerForm.name.trim()) {
      setMessage({ type: "danger", text: "Volunteer name is required." });
      return;
    }
    try {
      await createVolunteer({
        ...newVolunteerForm,
        preferredEventId: newVolunteerForm.preferredEventId ? Number(newVolunteerForm.preferredEventId) : null,
      });
      setNewVolunteerForm({
        name: "",
        email: "",
        phone: "",
        interests: "",
        availability: "",
        eventInterestType: "any",
        preferredEventId: "",
        message: "",
        approveNow: true,
      });
      setMessage({ type: "success", text: "Volunteer added." });
      await loadData();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to add volunteer." });
    }
  };

  const copyEventLink = async (eventId) => {
    if (!eventId) return;
    const link = `${window.location.origin}/events/${eventId}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const input = document.createElement("textarea");
        input.value = link;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setMessage({ type: "success", text: `${link} copied to the clipboard.` });
    } catch {
      window.prompt("Copy this event link:", link);
    }
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    setMessage({ type: "", text: "" });
    setGeneratedCredential("");
    if (!newAdminEmail) return;

    try {
      const response = await createAdminUser({ email: newAdminEmail.trim() });
      trackAdminActivity("admin_user_created", { email: newAdminEmail.trim() });
      const generatedPassword = extractGeneratedPassword(response);
      const createdEmail = String(response?.data?.email || newAdminEmail).trim();
      const credentialText = generatedPassword
        ? `New admin: ${createdEmail} | Generated password: ${generatedPassword}`
        : `New admin created: ${createdEmail} | Password was not returned by API response.`;
      setGeneratedCredential(credentialText);
      setShowCredentialModal(true);
      setNewAdminEmail("");
      await loadData();
      loadAnalytics();
      setMessage({ type: generatedPassword ? "success" : "warning", text: generatedPassword ? "Admin user created." : "Admin created, but no generated password was returned." });
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to create admin user." });
    }
  };

  const handleResetPassword = async (userId) => {
    if (Number(userId) === 1) {
      setMessage({ type: "warning", text: "Primary admin account password reset is locked." });
      return;
    }
    setGeneratedCredential("");
    try {
      const response = await resetAdminPassword({ userId });
      const targetUser = adminUsers.find((admin) => Number(admin.id) === Number(userId));
      trackAdminActivity("admin_password_reset", {
        targetUserId: userId,
        targetEmail: targetUser?.email || "",
      });
      const generatedPassword = extractGeneratedPassword(response);
      const email = response.data?.email || "Admin user";
      const credentialText = generatedPassword
        ? `Password reset for ${email} | New password: ${generatedPassword}`
        : `Password reset for ${email} | Password was not returned by API response.`;
      setGeneratedCredential(credentialText);
      setShowCredentialModal(true);
      setMessage({ type: generatedPassword ? "success" : "warning", text: generatedPassword ? "Password reset complete." : "Password reset, but no generated password was returned." });
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to reset password." });
    }
  };

  const handleOrgContactChange = (event) => {
    const { name, value } = event.target;
    setOrgContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveOrgContact = async (event) => {
    event.preventDefault();
    try {
      await updateOrgContactInfo(orgContactForm);
      setMessage({ type: "success", text: "Organization contact info updated." });
      await loadData();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update organization info." });
    }
  };

  const handleGroupMemberFieldChange = (event) => {
    const { name, value } = event.target;
    setGroupMemberForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetGroupMemberEditor = () => {
    setGroupMemberForm(defaultGroupMemberForm);
    setEditingGroupMemberId(null);
  };

  const beginEditGroupMember = (member) => {
    setEditingGroupMemberId(Number(member.id));
    setGroupMemberForm({
      name: member.name || "",
      role: member.role || "",
      phone: member.phone || "",
      email: member.email || "",
      title: member.title || "",
      displayOrder: Number(member.displayOrder || 0),
    });
  };

  const handleSaveGroupMember = async (event) => {
    event.preventDefault();
    try {
      if (editingGroupMemberId) {
        await updateGroupMember({ id: editingGroupMemberId, ...groupMemberForm });
        trackAdminActivity("group_member_updated", { memberId: editingGroupMemberId });
        setMessage({ type: "success", text: "Group member updated." });
      } else {
        await createGroupMember(groupMemberForm);
        trackAdminActivity("group_member_created", { name: groupMemberForm.name || "" });
        setMessage({ type: "success", text: "Group member added." });
      }
      resetGroupMemberEditor();
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to save group member." });
    }
  };

  const handleDeleteGroupMember = async (id) => {
    if (!window.confirm("Delete this group member?")) return;
    try {
      await deleteGroupMember(id);
      trackAdminActivity("group_member_deleted", { memberId: id });
      if (Number(editingGroupMemberId) === Number(id)) {
        resetGroupMemberEditor();
      }
      setMessage({ type: "success", text: "Group member removed." });
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to delete group member." });
    }
  };

  const handleMoveGroupMember = async (member, direction) => {
    const sorted = [...groupMembers].sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
    const currentIndex = sorted.findIndex((item) => Number(item.id) === Number(member.id));
    if (currentIndex < 0) return;
    const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const current = sorted[currentIndex];
    const swap = sorted[swapIndex];

    try {
      await updateGroupMember({ ...current, displayOrder: Number(swap.displayOrder || 0) });
      await updateGroupMember({ ...swap, displayOrder: Number(current.displayOrder || 0) });
      trackAdminActivity("group_member_reordered", { memberId: member.id, direction });
      setMessage({ type: "success", text: "Group member order updated." });
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to reorder group members." });
    }
  };

  const handleFounderStoryFieldChange = (event) => {
    const { name, value } = event.target;
    setFounderStoryForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetFounderStoryEditor = () => {
    setFounderStoryForm(defaultFounderStoryForm);
    setEditingFounderStoryId(null);
  };

  const beginEditFounderStory = (story) => {
    setEditingFounderStoryId(Number(story.id));
    setFounderStoryForm({
      storyText: story.storyText || "",
      founderName: story.founderName || "",
      founderTitle: story.founderTitle || "",
    });
  };

  const handleSaveFounderStory = async (event) => {
    event.preventDefault();
    try {
      if (editingFounderStoryId) {
        await updateFounderStory({ id: editingFounderStoryId, ...founderStoryForm });
        trackAdminActivity("founder_story_updated", { founderStoryId: editingFounderStoryId });
        setMessage({ type: "success", text: "Founder story updated." });
      } else {
        await createFounderStory(founderStoryForm);
        trackAdminActivity("founder_story_created", { founderName: founderStoryForm.founderName || "" });
        setMessage({ type: "success", text: "Founder story added." });
      }
      resetFounderStoryEditor();
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to save founder story." });
    }
  };

  const handleDeleteFounderStory = async (id) => {
    if (!window.confirm("Delete this founder story?")) return;
    try {
      await deleteFounderStory(id);
      trackAdminActivity("founder_story_deleted", { founderStoryId: id });
      if (Number(editingFounderStoryId) === Number(id)) {
        resetFounderStoryEditor();
      }
      setMessage({ type: "success", text: "Founder story removed." });
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to delete founder story." });
    }
  };


  const handleMissionSupporterFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    setMissionSupporterForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const resetMissionSupporterEditor = () => {
    setMissionSupporterForm(defaultMissionSupporterForm);
    setEditingMissionSupporterId(null);
  };

  const beginEditMissionSupporter = (supporter) => {
    setEditingMissionSupporterId(Number(supporter.id));
    setMissionSupporterForm({
      supporterName: supporter.supporterName || "",
      supporterSection: supporter.supporterSection || "heroes",
      description: supporter.description || "",
      isActive: Boolean(Number(supporter.isActive ?? 1)),
    });
  };

  const handleSaveMissionSupporter = async (event) => {
    event.preventDefault();
    try {
      if (editingMissionSupporterId) {
        await updateMissionSupporter({ id: editingMissionSupporterId, ...missionSupporterForm });
        trackAdminActivity("mission_supporter_updated", { supporterId: editingMissionSupporterId });
        setMessage({ type: "success", text: "Mission supporter updated." });
      } else {
        await createMissionSupporter(missionSupporterForm);
        trackAdminActivity("mission_supporter_created", { supporterName: missionSupporterForm.supporterName || "" });
        setMessage({ type: "success", text: "Mission supporter added." });
      }
      resetMissionSupporterEditor();
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to save mission supporter." });
    }
  };

  const handleDeleteMissionSupporter = async (id) => {
    if (!window.confirm("Delete this mission supporter?")) return;
    try {
      await deleteMissionSupporter(id);
      trackAdminActivity("mission_supporter_deleted", { supporterId: id });
      if (Number(editingMissionSupporterId) === Number(id)) {
        resetMissionSupporterEditor();
      }
      setMessage({ type: "success", text: "Mission supporter removed." });
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to delete mission supporter." });
    }
  };

  const handleSupportLinkChange = (event) => {
    const { name, value } = event.target;
    setSupportLinksForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSupportLinks = async (event) => {
    event.preventDefault();
    try {
      await updateSupportLinks({
        links: [
          {
            provider: "paypal",
            url: supportLinksForm.paypalUrl || "",
            description: supportLinksForm.paypalDescription || "",
          },
          {
            provider: "venmo",
            url: supportLinksForm.venmoUrl || "",
            description: supportLinksForm.venmoDescription || "",
          },
        ],
      });
      trackAdminActivity("support_links_updated");
      setMessage({ type: "success", text: "Support links updated." });
      await loadData();
      loadAnalytics();
    } catch (error) {
      setMessage({ type: "danger", text: error.message || "Failed to update support links." });
    }
  };

  return (
    <section>
      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <h1 className="section-title mb-3">Admin Dashboard</h1>
          <p className="text-muted-soft mb-0">Review submissions, manage calendar events, and control admin accounts.</p>
        </Card.Body>
      </Card>

      {message.text && <Alert variant={message.type || "info"}>{message.text}</Alert>}
      {generatedCredential && <Alert variant="info">{generatedCredential}</Alert>}

      <Row className="g-4">
        {/* Main submissions panel */}
        <Col lg={8}>
          <Card className="glass-card text-light">
            <Card.Body>
              <Nav
                variant="tabs"
                activeKey={activeTab}
                onSelect={setActiveTab}
                className="mb-4"
              >
                <Nav.Item>
                  <Nav.Link eventKey="contacts">
                    Contacts <Badge bg="secondary">{pendingContactMessages.length}</Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="volunteers">
                    Volunteers <Badge bg="secondary">{pendingVolunteers.length}</Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="resources">
                    Resources <Badge bg="secondary">{pendingSubmissions.length}</Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="assignments">Assign Volunteers</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="events">
                    Event Suggestions <Badge bg="secondary">{pendingSubmittedEvents.length}</Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="directory">Org & Members</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="mission-supporters">Mission Supporters</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="archive">Archives</Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Resources Tab */}
              {activeTab === "resources" && (
                <div>
                  <h3 className="h5 mb-3">New Pending Resource Submissions</h3>
                  {pendingSubmissions.length === 0 ? (
                    <p className="text-muted-soft">No pending submissions.</p>
                  ) : (
                    <div className="table-responsive mb-4">
                      <Table
                        variant="dark"
                        hover
                        size="sm"
                      >
                        <thead>
                          <tr>
                            <th>Organization</th>
                            <th>Summary</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingSubmissions.map((submission) => (
                            <tr key={submission.id}>
                              <td>
                                <strong>{submission.name || submission.organizationName}</strong>
                                <div className="small text-muted-soft">{submission.organizationName || "No organization name provided"}</div>
                                <div className="small text-muted-soft">{submission.email || "No email provided"}</div>
                              </td>
                              <td>
                                <div className="small text-muted-soft">Submitted: {submission.createdAt ? new Date(submission.createdAt).toLocaleString() : "Unknown"}</div>
                                <div className="small text-muted-soft">Phone: {submission.phone || submission.resourcePhone || "Not provided"}</div>
                                <div className="small text-muted-soft">Address: {submission.address || "Not provided"}</div>
                                <div className="small text-muted-soft">Categories: {(submission.categoryNames || []).length || 0}</div>
                              </td>
                              <td>
                                <Badge bg={submission.status === "pending" ? "warning" : submission.status === "approved" ? "success" : "danger"}>{submission.status}</Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-1 flex-wrap">
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => openSubmissionEditor(submission)}
                                  >
                                    Review
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                    <h3 className="h5 mb-0">All Resources (Approved, Pending, Rejected)</h3>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => setShowCategoryManagerModal(true)}
                      >
                        Manage Category Tree
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleCreateResource}
                      >
                        Add Resource
                      </Button>
                    </div>
                  </div>
                  {sortedResources.length === 0 ? (
                    <p className="text-muted-soft">No resources available.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        variant="dark"
                        hover
                        size="sm"
                      >
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Website</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedResources.map((res) => (
                            <tr key={res.id}>
                              <td>{res.name}</td>
                              <td>{res.phone}</td>
                              <td>
                                {res.website ? (
                                  <a
                                    href={res.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-info"
                                  >
                                    link
                                  </a>
                                ) : (
                                  <span className="text-muted-soft">n/a</span>
                                )}
                              </td>
                              <td>
                                <Badge bg={res.status === "approved" ? "success" : res.status === "pending" ? "warning" : "danger"}>{res.status}</Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleEditResource(res)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteResource(res.id)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Messages Tab */}
              {activeTab === "contacts" && (
                <div>
                  <h3 className="h5 mb-3">Pending Contact Messages</h3>
                  {contactMessages.length === 0 ? (
                    <p className="text-muted-soft">No pending contact messages.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        variant="dark"
                        hover
                        size="sm"
                      >
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Message</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contactMessages.map((msg) => (
                            <tr key={msg.id}>
                              <td>{msg.name}</td>
                              <td>{msg.email}</td>
                              <td
                                className="small text-muted-soft text-truncate"
                                style={{ maxWidth: "200px" }}
                              >
                                {msg.message}
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    setEditingSubmission(msg);
                                    setEditingType("contact");
                                  }}
                                >
                                  Review
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}

              {/* Volunteers Tab */}
              {activeTab === "volunteers" && (
                <div>
                  <Card className="glass-card text-light mb-3">
                    <Card.Body>
                      <h4 className="h6 mb-3">Add Volunteer Manually</h4>
                      <Form onSubmit={handleCreateVolunteer}>
                        <Row className="g-2">
                          <Col md={6}>
                            <Form.Control
                              placeholder="Name"
                              name="name"
                              value={newVolunteerForm.name}
                              onChange={handleNewVolunteerChange}
                              required
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              placeholder="Email"
                              name="email"
                              value={newVolunteerForm.email}
                              onChange={handleNewVolunteerChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              placeholder="Phone"
                              name="phone"
                              value={newVolunteerForm.phone}
                              onChange={handleNewVolunteerChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Control
                              placeholder="Availability"
                              name="availability"
                              value={newVolunteerForm.availability}
                              onChange={handleNewVolunteerChange}
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Select
                              name="eventInterestType"
                              value={newVolunteerForm.eventInterestType}
                              onChange={handleNewVolunteerChange}
                            >
                              <option value="any">Any Event</option>
                              <option value="specific">Specific Event</option>
                              <option value="specific_and_others">Specific + Others</option>
                            </Form.Select>
                          </Col>
                          {newVolunteerForm.eventInterestType !== "any" && (
                            <Col md={6}>
                              <Form.Select
                                name="preferredEventId"
                                value={newVolunteerForm.preferredEventId}
                                onChange={handleNewVolunteerChange}
                              >
                                <option value="">Select preferred event</option>
                                {events.map((evt) => (
                                  <option
                                    key={`manual-vol-event-${evt.id}`}
                                    value={evt.id}
                                  >
                                    {evt.title} - {new Date(evt.startDate).toLocaleDateString()}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                          )}
                          <Col md={12}>
                            <Form.Control
                              placeholder="Interests"
                              name="interests"
                              value={newVolunteerForm.interests}
                              onChange={handleNewVolunteerChange}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              placeholder="Notes"
                              name="message"
                              value={newVolunteerForm.message}
                              onChange={handleNewVolunteerChange}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Check
                              type="checkbox"
                              label="Mark as approved now"
                              name="approveNow"
                              checked={newVolunteerForm.approveNow}
                              onChange={handleNewVolunteerChange}
                            />
                          </Col>
                        </Row>
                        <Button
                          type="submit"
                          className="cta-btn mt-3"
                          size="sm"
                        >
                          Add Volunteer
                        </Button>
                      </Form>
                    </Card.Body>
                  </Card>
                  <h3 className="h5 mb-3">Pending Volunteer Applications</h3>
                  {volunteers.length === 0 ? (
                    <p className="text-muted-soft">No pending volunteer applications.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        variant="dark"
                        hover
                        size="sm"
                      >
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Interests</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {volunteers.map((vol) => (
                            <tr key={vol.id}>
                              <td>{vol.name}</td>
                              <td>{vol.email}</td>
                              <td className="small">
                                {vol.phone ? (
                                  <div className="d-flex align-items-center gap-1">
                                    <span>{vol.phone}</span>
                                    <a
                                      href={buildTelHref(vol.phone)}
                                      className="btn btn-outline-info btn-sm py-0 px-1"
                                      title="Call"
                                    >
                                      &#128222;
                                    </a>
                                    <a
                                      href={buildSmsHref(vol.phone)}
                                      className="btn btn-outline-secondary btn-sm py-0 px-1"
                                      title="Text"
                                    >
                                      Text
                                    </a>
                                  </div>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td
                                className="small text-muted-soft text-truncate"
                                style={{ maxWidth: "200px" }}
                              >
                                {vol.interests}
                              </td>
                              <td>
                                <div className="d-flex gap-1 flex-wrap">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => openVolunteerReview(vol)}
                                  >
                                    Review
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                  <h3 className="h5 mt-4 mb-3">Approved Volunteers</h3>
                  {approvedVolunteers.length === 0 ? (
                    <p className="text-muted-soft">No approved volunteers yet.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        variant="dark"
                        hover
                        size="sm"
                      >
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Interests</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {approvedVolunteers.map((vol) => (
                            <tr key={`approved-volunteers-tab-${vol.id}`}>
                              <td>{vol.name}</td>
                              <td className="small">{vol.email || "N/A"}</td>
                              <td className="small">
                                {vol.phone ? (
                                  <div className="d-flex align-items-center gap-1">
                                    <span>{vol.phone}</span>
                                    <a
                                      href={buildTelHref(vol.phone)}
                                      className="btn btn-outline-info btn-sm py-0 px-1"
                                      title="Call"
                                    >
                                      &#128222;
                                    </a>
                                    <a
                                      href={buildSmsHref(vol.phone)}
                                      className="btn btn-outline-secondary btn-sm py-0 px-1"
                                      title="Text"
                                    >
                                      Text
                                    </a>
                                  </div>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td
                                className="small text-muted-soft text-truncate"
                                style={{ maxWidth: "200px" }}
                              >
                                {vol.interests}
                              </td>
                              <td>
                                <Badge bg="success">approved</Badge>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => openVolunteerReview(vol)}
                                >
                                  Edit
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}

              {/* Volunteer Assignments Tab */}
              {activeTab === "assignments" && (
                <div>
                  <h3 className="h5 mb-3">Event Coordination</h3>
                  <Row className="g-2 mb-3">
                    <Col md={8}>
                      <Form.Select
                        size="sm"
                        value={coordinationEventId}
                        onChange={(e) => handleCoordinationEventChange(e.target.value)}
                      >
                        <option value="">Select event to coordinate...</option>
                        {events.map((evt) => (
                          <option
                            key={`coordination-${evt.id}`}
                            value={evt.id}
                          >
                            {evt.title} - {new Date(evt.startDate).toLocaleDateString()}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex gap-2">
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          disabled={!coordinationEventId}
                          value={coordinationEventId ? events.find((evt) => String(evt.id) === String(coordinationEventId))?.neededVolunteers || 0 : 0}
                          onChange={(e) =>
                            setEvents((prev) =>
                              prev.map((evt) =>
                                String(evt.id) === String(coordinationEventId)
                                  ? { ...evt, neededVolunteers: Number(e.target.value || 0) }
                                  : evt,
                              ),
                            )
                          }
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-warning"
                          disabled={!coordinationEventId}
                          onClick={handleSaveNeededSlots}
                        >
                          Save Slots
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  {coordinationEventId && (
                    <Card className="glass-card text-light mb-4">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h4 className="h6 mb-0">Assigned Volunteers</h4>
                          <div className="d-flex gap-2 align-items-center">
                            {selectedNeededVolunteers > 0 && (
                              <Badge bg={overAssignedCount > 0 ? "danger" : remainingVolunteerSlots > 0 ? "warning" : "success"}>
                                {overAssignedCount > 0
                                  ? `Overbooked by ${overAssignedCount}`
                                  : remainingVolunteerSlots > 0
                                    ? `${remainingVolunteerSlots} more needed`
                                    : "Fully staffed"}
                              </Badge>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-info"
                              onClick={handleExportEventVolunteerCsv}
                              disabled={eventVolunteers.length === 0}
                            >
                              Export CSV
                            </Button>
                          </div>
                        </div>
                        {selectedNeededVolunteers > 0 ? (
                          <Alert
                            variant={overAssignedCount > 0 ? "danger" : remainingVolunteerSlots > 0 ? "warning" : "success"}
                            className="py-2 px-3 mb-3"
                          >
                            {overAssignedCount > 0
                              ? `Overbooked by ${overAssignedCount}. Remove assignments or increase needed volunteers.`
                              : remainingVolunteerSlots > 0
                                ? `${remainingVolunteerSlots} more volunteer${remainingVolunteerSlots === 1 ? "" : "s"} needed.`
                                : "Fully staffed."}
                          </Alert>
                        ) : (
                          <Alert
                            variant="secondary"
                            className="py-2 px-3 mb-3"
                          >
                            Slots are set to 0. Set needed slots if this event requires volunteers.
                          </Alert>
                        )}
                        {eventVolunteers.length === 0 ? (
                          <p className="small text-muted-soft mb-0">No volunteers assigned yet.</p>
                        ) : (
                          <div className="table-responsive">
                            <Table
                              variant="dark"
                              hover
                              size="sm"
                              className="mb-0"
                            >
                              <thead>
                                <tr>
                                  <th>Volunteer</th>
                                  <th>Actions</th>
                                  <th>Time</th>
                                  <th>Job</th>
                                  <th>What To Bring</th>
                                </tr>
                              </thead>
                              <tbody>
                                {eventVolunteers.map((item) => (
                                  <tr key={`assigned-${item.id}`}>
                                    <td>
                                      <div className="fw-semibold small">{item.name}</div>
                                      <div className="small text-muted-soft">{item.email || "No email"}</div>
                                    </td>
                                    <td className="text-end">
                                      <div className="d-flex flex-column flex-md-row gap-1 justify-content-end">
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline-success"
                                          onClick={() => handleUpdateAssignedVolunteerDetails(item)}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => handleUnassignVolunteer(item.volunteer_id)}
                                        >
                                          Remove
                                        </Button>
                                      </div>
                                    </td>
                                    <td style={{ minWidth: "150px" }}>
                                      <Form.Control
                                        size="sm"
                                        type="time"
                                        value={item.assigned_time || ""}
                                        onChange={(e) => handleEditEventVolunteerField(item.volunteer_id, "assigned_time", e.target.value)}
                                      />
                                    </td>
                                    <td style={{ minWidth: "180px" }}>
                                      <Form.Control
                                        size="sm"
                                        value={item.role || ""}
                                        onChange={(e) => handleEditEventVolunteerField(item.volunteer_id, "role", e.target.value)}
                                        placeholder="Role / Job"
                                      />
                                    </td>
                                    <td style={{ minWidth: "220px" }}>
                                      <Form.Control
                                        size="sm"
                                        value={item.bring_items || ""}
                                        onChange={(e) => handleEditEventVolunteerField(item.volunteer_id, "bring_items", e.target.value)}
                                        placeholder="Items to bring"
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                  <h4 className="h6 mb-3">Assign Volunteers to Selected Event</h4>
                  <Form
                    onSubmit={handleVolunteerAssignment}
                    className="mb-4"
                  >
                    <Row>
                      <Col sm={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Select Event</Form.Label>
                          <Form.Select
                            size="sm"
                            value={volAssignmentForm.eventId}
                            onChange={(e) => handleCoordinationEventChange(e.target.value)}
                          >
                            <option value="">Choose an event...</option>
                            {events.map((evt) => (
                              <option
                                key={evt.id}
                                value={evt.id}
                              >
                                {evt.title} - {new Date(evt.startDate).toLocaleDateString()}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Select Volunteer</Form.Label>
                          <div className="d-flex gap-2 mb-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={assignmentFilter === "preferred" ? "warning" : "outline-warning"}
                              disabled={!assignmentEventId}
                              onClick={() => {
                                setAssignmentFilter((prev) => (prev === "preferred" ? "all" : "preferred"));
                                setVolAssignmentForm((prev) => ({ ...prev, volunteerId: "" }));
                              }}
                            >
                              {assignmentFilter === "preferred"
                                ? "Showing Preferred Matches"
                                : `Filter Preferred (${preferredMatchCount})`}
                            </Button>
                            {assignmentFilter === "preferred" && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  setAssignmentFilter("all");
                                  setVolAssignmentForm((prev) => ({ ...prev, volunteerId: "" }));
                                }}
                              >
                                Show All
                              </Button>
                            )}
                          </div>
                          <Form.Select
                            size="sm"
                            value={volAssignmentForm.volunteerId}
                            onChange={(e) => setVolAssignmentForm({ ...volAssignmentForm, volunteerId: e.target.value })}
                          >
                            <option value="">Choose a volunteer...</option>
                            {assignableVolunteers.map((vol) => (
                              <option
                                key={vol.id}
                                value={vol.id}
                              >
                                {vol.name} ({vol.email})
                                {Number(vol.preferred_event_id || 0) === assignmentEventId ? " - Preferred Match" : ""}
                              </option>
                            ))}
                          </Form.Select>
                          {assignableVolunteers.length === 0 && (
                            <Form.Text className="text-muted-soft">
                              No available volunteers for the current filter.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Time (Optional)</Form.Label>
                          <Form.Control
                            size="sm"
                            type="time"
                            value={volAssignmentForm.assignedTime}
                            onChange={(e) => setVolAssignmentForm({ ...volAssignmentForm, assignedTime: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Role (Optional)</Form.Label>
                          <Form.Control
                            size="sm"
                            placeholder="e.g., Registration, Check-in"
                            value={volAssignmentForm.role}
                            onChange={(e) => setVolAssignmentForm({ ...volAssignmentForm, role: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>What To Bring (Optional)</Form.Label>
                          <Form.Control
                            size="sm"
                            placeholder="e.g., Gloves, water"
                            value={volAssignmentForm.bringItems}
                            onChange={(e) => setVolAssignmentForm({ ...volAssignmentForm, bringItems: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Button
                      type="submit"
                      className="cta-btn"
                      size="sm"
                    >
                      Assign Volunteer
                    </Button>
                  </Form>

                  {coordinationEventId && (
                    <Card className="glass-card text-light mb-4">
                      <Card.Body>
                        <h4 className="h6 mb-3">Volunteer Email Blast</h4>
                        <Form onSubmit={handleSendEventVolunteerBlast}>
                          <Form.Group className="mb-2">
                            <Form.Label>Subject</Form.Label>
                            <Form.Control
                              size="sm"
                              value={emailBlastForm.subject}
                              onChange={(e) => setEmailBlastForm((prev) => ({ ...prev, subject: e.target.value }))}
                              placeholder="Event update"
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label>Message</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={emailBlastForm.message}
                              onChange={(e) => setEmailBlastForm((prev) => ({ ...prev, message: e.target.value }))}
                              placeholder="Write your email to assigned volunteers..."
                              required
                            />
                          </Form.Group>
                          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                            <small className="text-muted-soft">
                              Sent from the configured server no-reply address.
                            </small>
                            <Button
                              type="submit"
                              size="sm"
                              variant="outline-warning"
                            >
                              Send Blast
                            </Button>
                          </div>
                        </Form>
                        <hr className="border-secondary my-3" />
                        <h5 className="small mb-2">Recent Sends</h5>
                        {eventEmailLogs.length === 0 ? (
                          <p className="small text-muted-soft mb-0">No email sends logged yet for this event.</p>
                        ) : (
                          <div className="table-responsive">
                            <Table
                              variant="dark"
                              size="sm"
                              className="mb-0"
                            >
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Subject</th>
                                  <th>Sent</th>
                                  <th>Failed</th>
                                  <th>From</th>
                                </tr>
                              </thead>
                              <tbody>
                                {eventEmailLogs.map((log) => (
                                  <tr key={`event-email-log-${log.id}`}>
                                    <td className="small">{new Date(log.created_at).toLocaleString()}</td>
                                    <td className="small">{log.subject}</td>
                                    <td className="small">{log.sent_count}</td>
                                    <td className="small">{log.failed_count}</td>
                                    <td className="small">{log.from_email || "N/A"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  )}

                  {approvedVolunteers.length === 0 ? (
                    <Alert variant="info">No approved volunteers yet. Review and approve volunteers first.</Alert>
                  ) : (
                    <>
                      <h4 className="h6 mt-4 mb-3">Approved Volunteers</h4>
                      <div className="table-responsive">
                        <Table
                          variant="dark"
                          hover
                          size="sm"
                        >
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Interests</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {approvedVolunteers.map((vol) => (
                              <tr key={vol.id}>
                                <td>{vol.name}</td>
                                <td className="small">{vol.email}</td>
                                <td className="small">
                                  {vol.phone ? (
                                    <div className="d-flex align-items-center gap-1">
                                      <span>{vol.phone}</span>
                                      <a
                                        href={buildTelHref(vol.phone)}
                                        className="btn btn-outline-info btn-sm py-0 px-1"
                                        title="Call"
                                      >
                                        &#128222;
                                      </a>
                                      <a
                                        href={buildSmsHref(vol.phone)}
                                        className="btn btn-outline-secondary btn-sm py-0 px-1"
                                        title="Text"
                                      >
                                        Text
                                      </a>
                                    </div>
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                                <td
                                  className="small text-muted-soft text-truncate"
                                  style={{ maxWidth: "150px" }}
                                >
                                  {vol.interests}
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => openVolunteerReview(vol)}
                                  >
                                    Edit
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "events" && (
                <div>
                  <h3 className="h5 mb-3">User Submitted Event Suggestions</h3>
                  {submittedEvents.length === 0 ? (
                    <p className="text-muted-soft">No user submitted events yet.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table variant="dark" hover size="sm">
                        <thead>
                          <tr>
                            <th>Event</th>
                            <th>Submitter</th>
                            <th>Status</th>
                            <th>Need Volunteers</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {submittedEvents.map((evt) => (
                            <tr key={`submitted-event-${evt.id}`}>
                              <td>
                                <div className="fw-semibold">{evt.title || "Untitled"}</div>
                                <div className="small text-muted-soft">{formatEventDateRange(evt)}</div>
                              </td>
                              <td className="small">
                                <div>{evt.submitterName || "N/A"}</div>
                                <div>{evt.submitterEmail || "N/A"}</div>
                              </td>
                              <td>
                                <Badge bg={evt.approvalStatus === "pending" ? "warning" : evt.approvalStatus === "approved" ? "success" : "danger"}>{evt.approvalStatus || "pending"}</Badge>
                              </td>
                              <td>{Number(evt.neededVolunteers || 0) > 0 ? evt.neededVolunteers : "No"}</td>
                              <td>
                                <div className="d-flex gap-1 flex-wrap">
                                  <Button size="sm" variant="outline-info" onClick={() => handleEditEvent(evt)}>
                                    Edit
                                  </Button>
                                  {(evt.approvalStatus || "pending") === "pending" && (
                                    <>
                                      <Button size="sm" variant="outline-warning" onClick={() => handleEventApprovalUpdate(evt, "pending")}>
                                        Pending
                                      </Button>
                                      <Button size="sm" variant="outline-success" onClick={() => handleEventApprovalUpdate(evt, "approved")}>
                                        Approve
                                      </Button>
                                      <Button size="sm" variant="outline-danger" onClick={() => handleEventApprovalUpdate(evt, "declined")}>
                                        Decline
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}

              {/* Org & Members Tab */}
              {activeTab === "directory" && (
                <div>
                  <Card className="glass-card text-light mb-3">
                    <Card.Body>
                      <h3 className="h5 mb-3">Organization Contact</h3>
                      <Form onSubmit={handleSaveOrgContact}>
                        <Row className="g-2">
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Street Address</Form.Label>
                              <Form.Control
                                name="streetAddress"
                                value={orgContactForm.streetAddress}
                                onChange={handleOrgContactChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>City</Form.Label>
                              <Form.Control
                                name="city"
                                value={orgContactForm.city}
                                onChange={handleOrgContactChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>State</Form.Label>
                              <Form.Control
                                name="state"
                                value={orgContactForm.state}
                                onChange={handleOrgContactChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>ZIP</Form.Label>
                              <Form.Control
                                name="zip"
                                value={orgContactForm.zip}
                                onChange={handleOrgContactChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Main Phone</Form.Label>
                              <Form.Control
                                name="phone"
                                value={orgContactForm.phone}
                                onChange={handleOrgContactChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Main Email</Form.Label>
                              <Form.Control
                                name="email"
                                value={orgContactForm.email}
                                onChange={handleOrgContactChange}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="mt-3">
                          <Button type="submit" variant="outline-warning" size="sm">
                            Save Organization Info
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>

                  <Card className="glass-card text-light">
                    <Card.Body>
                      <h3 className="h5 mb-3">Fremont Group Members</h3>
                      <Form onSubmit={handleSaveGroupMember} className="mb-3">
                        <Row className="g-2">
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Name</Form.Label>
                              <Form.Control name="name" value={groupMemberForm.name} onChange={handleGroupMemberFieldChange} />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Title</Form.Label>
                              <Form.Control name="title" value={groupMemberForm.title} onChange={handleGroupMemberFieldChange} />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Role</Form.Label>
                              <Form.Control name="role" value={groupMemberForm.role} onChange={handleGroupMemberFieldChange} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Phone</Form.Label>
                              <Form.Control name="phone" value={groupMemberForm.phone} onChange={handleGroupMemberFieldChange} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Email</Form.Label>
                              <Form.Control name="email" value={groupMemberForm.email} onChange={handleGroupMemberFieldChange} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Display Order</Form.Label>
                              <Form.Control type="number" min={0} name="displayOrder" value={groupMemberForm.displayOrder} onChange={handleGroupMemberFieldChange} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-3">
                          <Button type="submit" variant="outline-warning" size="sm">
                            {editingGroupMemberId ? "Save Member" : "Add Member"}
                          </Button>
                          {editingGroupMemberId && (
                            <Button type="button" variant="secondary" size="sm" onClick={resetGroupMemberEditor}>
                              Cancel Edit
                            </Button>
                          )}
                        </div>
                      </Form>

                      {groupMembers.length === 0 ? (
                        <p className="text-muted-soft mb-0">No group members yet.</p>
                      ) : (
                        <div className="table-responsive">
                          <Table variant="dark" hover size="sm" className="mb-0">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Title</th>
                                <th>Role</th>
                                <th>Phone</th>
                                <th>Email</th>
                                <th>Order</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupMembers.map((member) => (
                                <tr key={`admin-group-member-${member.id}`}>
                                  <td>{member.name || "N/A"}</td>
                                  <td>{member.title || "N/A"}</td>
                                  <td>{member.role || "N/A"}</td>
                                  <td>
                                    {member.phone ? (
                                      <div className="d-flex flex-column gap-1">
                                        <span>{member.phone}</span>
                                        <div className="d-flex gap-1 flex-wrap">
                                          <a
                                            href={buildTelHref(member.phone)}
                                            className="btn btn-outline-info btn-sm py-0 px-2"
                                            aria-label={`Call ${member.name || "group member"}`}
                                          >
                                            &#128222; Call
                                          </a>
                                          <a
                                            href={buildSmsHref(member.phone)}
                                            className="btn btn-outline-success btn-sm py-0 px-2"
                                            aria-label={`Text ${member.name || "group member"}`}
                                          >
                                            &#128172; Text
                                          </a>
                                        </div>
                                      </div>
                                    ) : (
                                      "N/A"
                                    )}
                                  </td>
                                  <td>{member.email || "N/A"}</td>
                                  <td>{Number(member.displayOrder || 0)}</td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Button size="sm" variant="outline-secondary" onClick={() => handleMoveGroupMember(member, "up")}>
                                        Up
                                      </Button>
                                      <Button size="sm" variant="outline-secondary" onClick={() => handleMoveGroupMember(member, "down")}>
                                        Down
                                      </Button>
                                      <Button size="sm" variant="outline-primary" onClick={() => beginEditGroupMember(member)}>
                                        Edit
                                      </Button>
                                      <Button size="sm" variant="outline-danger" onClick={() => handleDeleteGroupMember(member.id)}>
                                        Delete
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  <Card className="glass-card text-light mt-3">
                    <Card.Body>
                      <h3 className="h5 mb-3">Why we started FVG</h3>
                      <p className="small text-muted-soft mb-3">These entries populate the About page card only when at least one story exists.</p>
                      <Form onSubmit={handleSaveFounderStory} className="mb-3">
                        <Row className="g-2">
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Block of Text</Form.Label>
                              <Form.Control as="textarea" rows={4} name="storyText" value={founderStoryForm.storyText} onChange={handleFounderStoryFieldChange} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Name</Form.Label>
                              <Form.Control name="founderName" value={founderStoryForm.founderName} onChange={handleFounderStoryFieldChange} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Position / Title</Form.Label>
                              <Form.Control name="founderTitle" value={founderStoryForm.founderTitle} onChange={handleFounderStoryFieldChange} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-3">
                          <Button type="submit" variant="outline-warning" size="sm">
                            {editingFounderStoryId ? "Save Story" : "Add Story"}
                          </Button>
                          {editingFounderStoryId && (
                            <Button type="button" variant="secondary" size="sm" onClick={resetFounderStoryEditor}>
                              Cancel Edit
                            </Button>
                          )}
                        </div>
                      </Form>

                      {founderStories.length === 0 ? (
                        <p className="text-muted-soft mb-0">No founder stories yet.</p>
                      ) : (
                        <div className="table-responsive">
                          <Table variant="dark" hover size="sm" className="mb-0">
                            <thead>
                              <tr>
                                <th>Story</th>
                                <th>Name</th>
                                <th>Title</th>
                                <th>Last Updated</th>
                                <th>Updated By</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {founderStories.map((story) => (
                                <tr key={`admin-founder-story-${story.id}`}>
                                  <td style={{ minWidth: 280 }}>{story.storyText || "N/A"}</td>
                                  <td>{story.founderName || "N/A"}</td>
                                  <td>{story.founderTitle || "N/A"}</td>
                                  <td>{story.lastUpdatedAt ? new Date(story.lastUpdatedAt).toLocaleString() : "N/A"}</td>
                                  <td>{story.lastUpdatedBy || "N/A"}</td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Button size="sm" variant="outline-primary" onClick={() => beginEditFounderStory(story)}>
                                        Edit
                                      </Button>
                                      <Button size="sm" variant="outline-danger" onClick={() => handleDeleteFounderStory(story.id)}>
                                        Delete
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  <Card className="glass-card text-light mt-3">
                    <Card.Body>
                      <h3 className="h5 mb-3">Support Links (PayPal / Venmo)</h3>
                      <p className="small text-muted-soft mb-3">
                        Leave a URL blank to hide that provider from the public Support page.
                      </p>
                      <Form onSubmit={handleSaveSupportLinks}>
                        <Row className="g-2">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>PayPal URL</Form.Label>
                              <Form.Control
                                name="paypalUrl"
                                value={supportLinksForm.paypalUrl}
                                onChange={handleSupportLinkChange}
                                placeholder="https://paypal.me/yourname"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>PayPal Description</Form.Label>
                              <Form.Control
                                name="paypalDescription"
                                value={supportLinksForm.paypalDescription}
                                onChange={handleSupportLinkChange}
                                placeholder="Optional short text"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Venmo URL</Form.Label>
                              <Form.Control
                                name="venmoUrl"
                                value={supportLinksForm.venmoUrl}
                                onChange={handleSupportLinkChange}
                                placeholder="https://venmo.com/yourname"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Venmo Description</Form.Label>
                              <Form.Control
                                name="venmoDescription"
                                value={supportLinksForm.venmoDescription}
                                onChange={handleSupportLinkChange}
                                placeholder="Optional short text"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="mt-3">
                          <Button type="submit" variant="outline-warning" size="sm">
                            Save Support Links
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </div>
              )}


              {activeTab === "mission-supporters" && (
                <div>
                  <Card className="glass-card text-light mb-3">
                    <Card.Body>
                      <h3 className="h5 mb-2">Mission Supporters</h3>
                      <p className="small text-muted-soft mb-3">
                        Add people or businesses, choose where they appear, and they will display alphabetically on the public Mission Supporters page.
                      </p>
                      <Form onSubmit={handleSaveMissionSupporter} className="mb-3">
                        <Row className="g-2">
                          <Col md={5}>
                            <Form.Group>
                              <Form.Label>Business or Person Name</Form.Label>
                              <Form.Control name="supporterName" value={missionSupporterForm.supporterName} onChange={handleMissionSupporterFieldChange} required />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Show In</Form.Label>
                              <Form.Select name="supporterSection" value={missionSupporterForm.supporterSection} onChange={handleMissionSupporterFieldChange}>
                                <option value="heroes">Heroes Helping Heroes</option>
                                <option value="partners">Patriot Business Partners</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3} className="d-flex align-items-end">
                            <Form.Check
                              type="checkbox"
                              name="isActive"
                              label="Visible on site"
                              checked={missionSupporterForm.isActive}
                              onChange={handleMissionSupporterFieldChange}
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Optional Contribution Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={missionSupporterForm.description}
                                onChange={handleMissionSupporterFieldChange}
                                placeholder="Describe donated time, funds, supplies, services, or other support."
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-3">
                          <Button type="submit" variant="outline-warning" size="sm">
                            {editingMissionSupporterId ? "Save Supporter" : "Add Supporter"}
                          </Button>
                          {editingMissionSupporterId && (
                            <Button type="button" variant="secondary" size="sm" onClick={resetMissionSupporterEditor}>
                              Cancel Edit
                            </Button>
                          )}
                        </div>
                      </Form>

                      {missionSupporters.length === 0 ? (
                        <p className="text-muted-soft mb-0">No mission supporters yet.</p>
                      ) : (
                        <div className="table-responsive">
                          <Table variant="dark" hover size="sm" className="mb-0">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Section</th>
                                <th>Description</th>
                                <th>Visible</th>
                                <th>Last Updated</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {missionSupporters.map((supporter) => (
                                <tr key={`admin-mission-supporter-${supporter.id}`}>
                                  <td>{supporter.supporterName || "N/A"}</td>
                                  <td>{missionSupporterSectionLabels[supporter.supporterSection] || supporter.supporterSection || "N/A"}</td>
                                  <td style={{ minWidth: 240 }}>{supporter.description || "N/A"}</td>
                                  <td>{Number(supporter.isActive) ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                                  <td>{supporter.lastUpdatedAt ? new Date(supporter.lastUpdatedAt).toLocaleString() : "N/A"}</td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Button size="sm" variant="outline-primary" onClick={() => beginEditMissionSupporter(supporter)}>
                                        Edit
                                      </Button>
                                      <Button size="sm" variant="outline-danger" onClick={() => handleDeleteMissionSupporter(supporter.id)}>
                                        Delete
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              )}

              {/* Archive Tab */}
              {activeTab === "archive" && (
                <div>
                  <Nav
                    variant="pills"
                    activeKey={activeArchive}
                    onSelect={setActiveArchive}
                    className="mb-3"
                  >
                    <Nav.Item>
                      <Nav.Link eventKey="archive-contacts">Contacts ({archivedContacts.length})</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="archive-volunteers">Volunteers ({archivedVolunteers.length})</Nav.Link>
                    </Nav.Item>
                  </Nav>

                  {activeArchive === "archive-contacts" && (
                    <div>
                      <h4 className="h6 mb-3">Reviewed Contact Messages</h4>
                      {archivedContacts.length === 0 ? (
                        <p className="text-muted-soft">No archived contacts.</p>
                      ) : (
                        <div className="table-responsive">
                          <Table
                            variant="dark"
                            hover
                            size="sm"
                          >
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Notes</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {archivedContacts.map((msg) => (
                                <tr key={msg.id}>
                                  <td>{msg.name}</td>
                                  <td>
                                    <Badge bg={msg.review_status === "contacted" ? "success" : "secondary"}>{msg.review_status}</Badge>
                                  </td>
                                  <td className="small text-muted-soft">{msg.admin_notes || "N/A"}</td>
                                  <td className="small text-muted-soft">{new Date(msg.reviewed_at).toLocaleDateString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}

                  {activeArchive === "archive-volunteers" && (
                    <div>
                      <h4 className="h6 mb-3">Reviewed Volunteer Applications</h4>
                      {archivedVolunteers.length === 0 ? (
                        <p className="text-muted-soft">No archived volunteers.</p>
                      ) : (
                        <div className="table-responsive">
                          <Table
                            variant="dark"
                            hover
                            size="sm"
                          >
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Interests</th>
                                <th>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {archivedVolunteers.map((vol) => (
                                <tr key={vol.id}>
                                  <td>{vol.name}</td>
                                  <td>{vol.email}</td>
                                  <td>
                                    <Badge bg={vol.volunteer_status === "declined" ? "danger" : "success"}>
                                      {vol.volunteer_status || "approved"}
                                    </Badge>
                                  </td>
                                  <td
                                    className="small text-muted-soft text-truncate"
                                    style={{ maxWidth: "150px" }}
                                  >
                                    {vol.interests}
                                  </td>
                                  <td className="small text-muted-soft">{vol.admin_notes || "N/A"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right sidebar */}
        <Col lg={4}>
          <Card className="glass-card text-light mb-4">
            <Card.Body>
              <h2 className="h5 mb-3">Create Calendar Event</h2>
              <Form onSubmit={handleEventCreate}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Title</Form.Label>
                  <Form.Control
                    required
                    value={eventForm.title}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, title: e.target.value }))}
                    size="sm"
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Date & Time</Form.Label>
                  <Form.Control
                    required
                    type="datetime-local"
                    value={eventForm.startDate}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, startDate: e.target.value }))}
                    size="sm"
                  />
                </Form.Group>
                <Form.Check
                  type="switch"
                  id="createMultiDayEvent"
                  label="Multi-day event"
                  checked={Boolean(eventForm.isMultiDay)}
                  onChange={(e) =>
                    setEventForm((prev) => ({
                      ...prev,
                      isMultiDay: e.target.checked,
                      endDate: e.target.checked ? prev.endDate : "",
                    }))
                  }
                  className="mb-2 small"
                />
                {eventForm.isMultiDay && (
                  <Form.Group className="mb-2">
                    <Form.Label className="small">End Date & Time</Form.Label>
                    <Form.Control
                      required
                      type="datetime-local"
                      value={eventForm.endDate || ""}
                      onChange={(e) => setEventForm((prev) => ({ ...prev, endDate: e.target.value }))}
                      size="sm"
                    />
                  </Form.Group>
                )}
                <Form.Group className="mb-2">
                  <Form.Label className="small">Event Address</Form.Label>
                  <Form.Control
                    value={eventForm.eventAddress || ""}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, eventAddress: e.target.value }))}
                    size="sm"
                    placeholder="Street, City, State ZIP"
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Details</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={eventForm.details}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, details: e.target.value }))}
                    size="sm"
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Event Images</Form.Label>
                  {(eventForm.imageUrls || []).length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {(eventForm.imageUrls || []).map((url, index) => (
                        <div
                          key={`event-image-${index}`}
                          className="position-relative"
                        >
                          <img
                            src={url}
                            alt={`Event preview ${index + 1}`}
                            style={{ width: "84px", height: "84px", objectFit: "cover", borderRadius: "4px" }}
                          />}
                          <Button
                            type="button"
                            size="sm"
                            variant="danger"
                            className="position-absolute top-0 end-0 p-0"
                            style={{ width: "20px", height: "20px", lineHeight: "1" }}
                            onClick={() => removeEventImageAt(index)}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    size="sm"
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Event Video (optional, max 50MB)</Form.Label>
                  {eventForm.videoUrl && <video src={eventForm.videoUrl} className="event-gallery-main mb-2" controls muted />}
                  <Form.Control type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime" onChange={handleVideoUpload} size="sm" />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Needed Volunteers</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={eventForm.neededVolunteers}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, neededVolunteers: Number(e.target.value || 0) }))}
                    size="sm"
                  />
                </Form.Group>
                <Form.Check
                  type="switch"
                  id="publicEvent"
                  label="Public"
                  checked={eventForm.isPublic}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, isPublic: e.target.checked }))}
                  className="mb-2 small"
                />
                <Form.Check
                  type="switch"
                  id="createSpotlightEvent"
                  label="Spotlight"
                  checked={Boolean(eventForm.spotlight)}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, spotlight: e.target.checked }))}
                  className="mb-2 small"
                />
                <Button
                  type="submit"
                  className="cta-btn w-100"
                  size="sm"
                >
                  Save Event
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card className="glass-card text-light mb-4">
            <Card.Body>
              <h2 className="h5 mb-3">Manage Events</h2>
              {events.length === 0 ? (
                <p className="text-muted-soft small">No events created yet.</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {events.slice(0, 5).map((evt) => (
                    <div
                      key={evt.id}
                      className="border-bottom pb-2 small"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <strong className="small">{evt.title}</strong>
                          <br />
                          <span className="text-muted-soft">{formatEventDateRange(evt)}</span>
                          {evt.eventAddress && (
                            <>
                              <br />
                              <span className="text-muted-soft">{evt.eventAddress}</span>
                              {" "}
                              <a href={buildMapsHref(evt.eventAddress)} target="_blank" rel="noreferrer">
                                Map
                              </a>
                            </>
                          )}
                          <br />
                          <span className="text-muted-soft">
                            Last blast: {evt.lastEmailSentAt ? new Date(evt.lastEmailSentAt).toLocaleString() : "Never"}
                          </span>
                        </div>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="px-2"
                            onClick={() => copyEventLink(evt.id)}
                            title="Copy event link"
                          >
                            Link
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="p-0"
                            style={{ width: "28px", height: "28px" }}
                            onClick={() => handleEditEvent(evt)}
                            title="Edit"
                          >
                            ✎
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="p-0"
                            style={{ width: "28px", height: "28px" }}
                            onClick={() => handleDeleteEvent(evt.id)}
                            title="Delete"
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          <Card className="glass-card text-light">
            <Card.Body>
              <h2 className="h5 mb-3">Calendar View</h2>
              <Form.Control
                type="month"
                value={monthYear}
                onChange={(e) => setMonthYear(e.target.value)}
                size="sm"
                className="mb-3"
              />
              <div className="calendar-grid mb-2 text-muted-soft">
                {weekdayLabels.map((label) => (
                  <div
                    key={label}
                    className="text-center fw-semibold small"
                  >
                    {label}
                  </div>
                ))}
              </div>
              <div className="calendar-grid">
                {calendarDays.map((day, index) =>
                  day ? (
                    <button
                      type="button"
                      key={`${day.day}-${index}`}
                      className={`calendar-day ${day.events.length ? "has-event" : ""}`}
                      onClick={() => openAdminCalendarDay(day)}
                    >
                      <div className="small fw-semibold">{day.day}</div>
                      {day.events.slice(0, 2).map((eventItem) => (
                        <div
                          key={eventItem.id}
                          className="small text-muted-soft text-truncate"
                        >
                          {eventItem.title}
                        </div>
                      ))}
                    </button>
                  ) : (
                    <div
                      key={`empty-${index}`}
                      className="calendar-day opacity-50"
                    />
                  ),
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
            <Card className="glass-card text-light">
            <Card.Body>
              <h2 className="h5 mb-3">Manage Admin Accounts</h2>
              {generatedCredential && (
                <Alert variant="info" className="small py-2">
                  {generatedCredential}
                </Alert>
              )}
              <Form
                onSubmit={handleCreateAdmin}
                className="mb-3"
              >
                <Form.Group className="mb-2">
                  <Form.Label>Add New Admin</Form.Label>
                  <Form.Control
                    required
                    type="email"
                    placeholder="name@example.org"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    size="sm"
                  />
                </Form.Group>
                <Button
                  type="submit"
                  className="cta-btn w-100"
                  size="sm"
                >
                  Create Account
                </Button>
              </Form>
              <div className="table-responsive">
                <Table
                  variant="dark"
                  size="sm"
                  hover
                >
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.length === 0 && (
                      <tr>
                        <td
                          colSpan="2"
                          className="text-center small text-muted-soft"
                        >
                          No admin users yet
                        </td>
                      </tr>
                    )}
                    {adminUsers.map((adminUser) => (
                      <tr key={adminUser.id}>
                        <td className="small">{adminUser.email}</td>
                        <td>
                          {Number(adminUser.id) === 1 ? (
                            <Badge bg="secondary">Primary Admin (Locked)</Badge>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-warning"
                              onClick={() => handleResetPassword(adminUser.id)}
                            >
                              Reset
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          <Card className="glass-card text-light">
            <Card.Body>
              <h2 className="h5 mb-3">Site Analytics</h2>
              <Row className="g-2 mb-3">
                <Col lg={4}>
                  <Form.Group>
                    <Form.Label className="small">View</Form.Label>
                    <Form.Select size="sm" value={analyticsMetric} onChange={(e) => setAnalyticsMetric(e.target.value)}>
                      <option value="visits_by_user">Visits by User</option>
                      <option value="visits_by_page">Visits by Page</option>
                      <option value="new_vs_returning">New vs Returning Visitors</option>
                      <option value="peak_hours">Peak Hours</option>
                      <option value="peak_days">Peak Days</option>
                      <option value="submissions_by_type">Submission by Type</option>
                      <option value="event_moderation_sla">Event Moderation SLA</option>
                      <option value="app_usage">As an App Usage</option>
                      <option value="admin_activity">Admin User Activity</option>
                      <option value="admin_activity_by_action">Admin Activity by Action</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group>
                    <Form.Label className="small">Time Span</Form.Label>
                    <Form.Select size="sm" value={analyticsRange} onChange={(e) => setAnalyticsRange(e.target.value)}>
                      <option value="7d">Last 7 Days</option>
                      <option value="14d">Last 14 Days</option>
                      <option value="30d">Last 30 Days</option>
                      <option value="year">Year</option>
                      <option value="custom">Custom Range</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {analyticsRange === "custom" && (
                  <>
                    <Col lg={2}>
                      <Form.Group>
                        <Form.Label className="small">From</Form.Label>
                        <Form.Control size="sm" type="date" value={analyticsFrom} onChange={(e) => setAnalyticsFrom(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col lg={2}>
                      <Form.Group>
                        <Form.Label className="small">To</Form.Label>
                        <Form.Control size="sm" type="date" value={analyticsTo} onChange={(e) => setAnalyticsTo(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col lg={1} className="d-flex align-items-end">
                      <Button size="sm" variant="outline-info" className="w-100" onClick={loadAnalytics}>
                        Go
                      </Button>
                    </Col>
                  </>
                )}
              </Row>

              <div className="small text-muted-soft mb-2">
                {analyticsTitle || "Analytics"} | {analyticsWindow.startAt || "N/A"} to {analyticsWindow.endAt || "N/A"} | Total events: {Number(analyticsTotals.events || 0)}
              </div>

              {analyticsLoading ? (
                <div className="small text-muted-soft">Loading analytics...</div>
              ) : analyticsRows.length === 0 ? (
                <div className="small text-muted-soft">No analytics data for the selected window.</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {(() => {
                    const maxValue = Math.max(...analyticsRows.map((row) => Number(row.value || 0)), 1);
                    return analyticsRows.map((row, index) => {
                      const width = `${Math.max(4, (Number(row.value || 0) / maxValue) * 100)}%`;
                      return (
                        <div key={`analytics-row-${index}-${row.label}`}>
                          <div className="d-flex justify-content-between align-items-center small mb-1">
                            <span className="text-truncate pe-2">{row.label}</span>
                            <span className="fw-semibold">{Number(row.value || 0)}</span>
                          </div>
                          <div className="progress" style={{ height: "10px", background: "rgba(255,255,255,0.08)" }}>
                            <div className="progress-bar bg-info" role="progressbar" style={{ width }} aria-valuenow={Number(row.value || 0)} aria-valuemin="0" aria-valuemax={maxValue} />
                          </div>
                          {(row.lastSeen || row.browser || row.country) && (
                            <div className="small text-muted-soft mt-1">
                              {[row.lastSeen ? `Last seen: ${new Date(row.lastSeen).toLocaleString()}` : "", row.browser ? `Browser: ${row.browser}` : "", row.country ? `Country: ${row.country}` : ""]
                                .filter(Boolean)
                                .join(" | ")}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Submission Editor Modal */}
      <Modal
        show={!!submissionEditor}
        onHide={() => setSubmissionEditor(null)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Review Resource Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submissionEditor && (
            <Form>
              <Card className="resource-card text-light mb-3">
                <Card.Body className="small">
                  <h6 className="mb-2">Submission Summary</h6>
                  <Row className="g-2">
                    <Col md={6}><div><strong>Submission ID:</strong> {submissionEditor.id}</div></Col>
                    <Col md={6}><div><strong>Submitted:</strong> {submissionEditor.createdAt ? new Date(submissionEditor.createdAt).toLocaleString() : "Unknown"}</div></Col>
                    <Col md={6}><div><strong>Current Status:</strong> {submissionEditor.status || "pending"}</div></Col>
                    <Col md={6}><div><strong>Organization:</strong> {submissionEditor.organizationName || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Contact Name:</strong> {submissionEditor.contactName || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Contact Email:</strong> {submissionEditor.email || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Contact Phone:</strong> {submissionEditor.phone || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Resource Name:</strong> {submissionEditor.name || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Resource Phone:</strong> {submissionEditor.resourcePhone || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Resource Contact:</strong> {submissionEditor.contactPerson || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Website:</strong> {submissionEditor.website || "Not provided"}</div></Col>
                    <Col md={12}><div><strong>Address:</strong> {[submissionEditor.address, submissionEditor.zipcode].filter(Boolean).join(" ") || "Not provided"}</div></Col>
                    <Col md={6}><div><strong>Cost Scale:</strong> {Number(submissionEditor.costScale || 0)}</div></Col>
                    <Col md={6}><div><strong>Categories:</strong> {(submissionEditor.categoryNames || []).join(", ") || "None selected"}</div></Col>
                    <Col md={12}><div><strong>Description:</strong> {submissionEditor.description || "Not provided"}</div></Col>
                  </Row>
                </Card.Body>
              </Card>

              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Organization Name</Form.Label>
                    <Form.Control
                      name="organizationName"
                      value={submissionEditor.organizationName}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Contact Name</Form.Label>
                    <Form.Control
                      name="contactName"
                      value={submissionEditor.contactName}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Contact Email</Form.Label>
                    <Form.Control
                      name="email"
                      value={submissionEditor.email}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Contact Phone</Form.Label>
                    <Form.Control
                      name="phone"
                      value={submissionEditor.phone}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Resource Name</Form.Label>
                    <Form.Control
                      name="name"
                      value={submissionEditor.name}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Resource Phone</Form.Label>
                    <Form.Control
                      name="resourcePhone"
                      value={submissionEditor.resourcePhone}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Resource Contact</Form.Label>
                    <Form.Control
                      name="contactPerson"
                      value={submissionEditor.contactPerson}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Website</Form.Label>
                    <Form.Control
                      name="website"
                      value={submissionEditor.website}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>ZIP Code</Form.Label>
                    <Form.Control
                      name="zipcode"
                      value={submissionEditor.zipcode}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-2">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      name="address"
                      value={submissionEditor.address}
                      onChange={handleSubmissionEditorChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={submissionEditor.description}
                  onChange={handleSubmissionEditorChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Cost Scale (0-5)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={5}
                  name="costScale"
                  value={submissionEditor.costScale ?? 0}
                  onChange={handleSubmissionEditorChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Special Notes</Form.Label>
                <Form.Control
                  plaintext
                  readOnly
                  value={String(submissionEditor.customSingle?.Value || submissionEditor.customSingle?.value || "").trim() || "None provided"}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Additional Notes</Form.Label>
                <Form.Control
                  plaintext
                  readOnly
                  value={Array.isArray(submissionEditor.customList?.Values || submissionEditor.customList?.values)
                    ? (submissionEditor.customList?.Values || submissionEditor.customList?.values).join(", ") || "None provided"
                    : "None provided"}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Categories</Form.Label>
                <div className="d-flex flex-column gap-2">
                  {majorCategories.map((major) => {
                    const selectedIds = normalizeCategoryIds(submissionEditor.categoryIds || []);
                    const selectedMajor = selectedIds.includes(Number(major.id));
                    const children = childCategoriesByParent.get(Number(major.id)) || [];
                    return (
                      <div key={`submission-modal-major-${major.id}`}>
                        <Form.Check
                          id={`submission-modal-major-${major.id}`}
                          type="checkbox"
                          label={major.name}
                          checked={selectedMajor}
                          onChange={() => toggleEditorCategory(major.id)}
                        />
                        {children.length > 0 && (
                          <div className="d-flex flex-column gap-1 mt-1 ms-3">
                            {children.map((child) => {
                              const selectedChild = selectedIds.includes(Number(child.id));
                              return (
                                <Form.Check
                                  key={`submission-modal-child-${child.id}`}
                                  id={`submission-modal-child-${child.id}`}
                                  type="checkbox"
                                  label={child.name}
                                  checked={selectedChild}
                                  onChange={() => toggleEditorCategory(child.id)}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Admin Notes (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={submissionEditor.notes}
                  onChange={handleSubmissionEditorChange}
                  placeholder="Add review notes if needed."
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setSubmissionEditor(null)}
          >
            Close
          </Button>
          <Button
            variant="outline-warning"
            onClick={() => saveSubmissionEditor()}
          >
            Save
          </Button>
          <Button
            variant="outline-danger"
            onClick={() => saveSubmissionEditor("rejected")}
          >
            Save & Decline
          </Button>
          <Button
            variant="success"
            onClick={() => saveSubmissionEditor("approved")}
          >
            Save & Approve
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Admin Calendar Day Modal */}
      <Modal
        show={adminCalendarModalEvents.length > 0}
        onHide={() => {
          setAdminCalendarModalEvents([]);
          setAdminCalendarModalDate("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {adminCalendarModalDate ? new Date(`${adminCalendarModalDate}T12:00:00`).toLocaleDateString() : "Event Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {adminCalendarModalEvents.map((event) => (
            <Card
              key={`admin-cal-event-${event.id}`}
              className="mb-3 event-preview-card resource-card text-light"
            >
              <Card.Body className="event-preview-body">
                <AdminEventImageStrip
                  images={event.imageUrls?.length ? event.imageUrls : event.imageUrl ? [event.imageUrl] : []}
                  title={event.title}
                />
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-2">
                  <div>
                    <h5 className="h6 mb-1">{event.title}</h5>
                    <p className="small text-muted-soft mb-2">{formatEventDateRange(event)}</p>
                    {event.eventAddress && (
                      <p className="small text-muted-soft mb-2">
                        {event.eventAddress}{" "}
                        <a href={buildMapsHref(event.eventAddress)} target="_blank" rel="noreferrer">
                          Map
                        </a>
                      </p>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => copyEventLink(event.id)}
                    >
                      Copy Link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-info"
                      onClick={() => {
                        handleEditEvent(event);
                        setAdminCalendarModalEvents([]);
                        setAdminCalendarModalDate("");
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
                <p className="small mb-2">{event.details || "No details provided."}</p>
                {(() => {
                  const need = getVolunteerNeedSummary(event.neededVolunteers, event.assignedVolunteerCount || 0);
                  if (!need) return null;
                  return (
                    <Badge bg={need.bg} className="mb-2">
                      {need.text}
                    </Badge>
                  );
                })()}
                <div className="d-flex flex-wrap gap-2">
                  {(event.assignedVolunteers || []).length > 0 && (
                    <span className="small text-muted-soft fw-semibold align-self-center">Volunteers</span>
                  )}
                  {(event.assignedVolunteers || []).map((assignment, idx) => (
                    <Badge key={`admin-cal-vol-${event.id}-${idx}`} bg="secondary">
                      {assignment.publicName || "Volunteer"}
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          ))}
          {adminCalendarModalEvents.length === 0 && <Alert variant="secondary">No events for this day.</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setAdminCalendarModalEvents([]);
              setAdminCalendarModalDate("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Review Modal for Contact Messages */}
      <Modal
        show={editingSubmission && editingType === "contact"}
        onHide={() => setEditingSubmission(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Review Contact Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingSubmission && (
            <div>
              <p>
                <strong>From:</strong> {editingSubmission.name}
              </p>
              <p>
                <strong>Email:</strong> {editingSubmission.email}
              </p>
              <p>
                <strong>Message:</strong>
              </p>
              <p className="text-muted-soft">{editingSubmission.message}</p>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  size="sm"
                  defaultValue={editingSubmission.review_status || "pending"}
                >
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="contacted">Contacted</option>
                  <option value="not_contacted">Not Contacted</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Admin Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  defaultValue={editingSubmission.admin_notes || ""}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditingSubmission(null)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={(e) => {
              const statusSelect = e.target.parentElement.parentElement.querySelector("select");
              const notesInput = e.target.parentElement.parentElement.querySelector("textarea");
              handleContactUpdate(editingSubmission.id, statusSelect.value, notesInput.value);
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Review Modal for Volunteers */}
      <Modal
        show={editingSubmission && editingType === "volunteer"}
        onHide={() => {
          setEditingSubmission(null);
          setVolunteerReviewForm(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Review Volunteer Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingSubmission && volunteerReviewForm && (
            <Form>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      value={volunteerReviewForm.name}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={volunteerReviewForm.email}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      value={volunteerReviewForm.phone}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, phone: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Availability</Form.Label>
                    <Form.Control
                      value={volunteerReviewForm.availability}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, availability: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Interests</Form.Label>
                    <Form.Control
                      value={volunteerReviewForm.interests}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, interests: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Event Interest</Form.Label>
                    <Form.Select
                      value={volunteerReviewForm.eventInterestType}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, eventInterestType: e.target.value }))}
                    >
                      <option value="any">Any Event</option>
                      <option value="specific">Specific Event</option>
                      <option value="specific_and_others">Specific + Others</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {volunteerReviewForm.eventInterestType !== "any" && (
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Preferred Event</Form.Label>
                      <Form.Select
                        value={volunteerReviewForm.preferredEventId}
                        onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, preferredEventId: e.target.value }))}
                      >
                        <option value="">Select preferred event</option>
                        {events.map((evt) => (
                          <option
                            key={`review-vol-pref-${evt.id}`}
                            value={evt.id}
                          >
                            {evt.title} - {new Date(evt.startDate).toLocaleDateString()}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={volunteerReviewForm.message}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, message: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Admin Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={volunteerReviewForm.adminNotes}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, adminNotes: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Volunteer Status</Form.Label>
                    <Form.Select
                      value={volunteerReviewForm.volunteerStatus || "pending"}
                      onChange={(e) => setVolunteerReviewForm((prev) => ({ ...prev, volunteerStatus: e.target.value }))}
                    >
                      <option value="pending">pending</option>
                      <option value="approved">approved</option>
                      <option value="declined">declined</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setEditingSubmission(null);
              setVolunteerReviewForm(null);
            }}
          >
            Close
          </Button>
          <Button
            variant={volunteerReviewForm?.volunteerStatus === "declined" ? "outline-danger" : volunteerReviewForm?.volunteerStatus === "approved" ? "success" : "outline-primary"}
            onClick={() =>
              handleVolunteerUpdate({
                id: editingSubmission.id,
                ...volunteerReviewForm,
                preferredEventId: volunteerReviewForm.preferredEventId ? Number(volunteerReviewForm.preferredEventId) : null,
                volunteerStatus: volunteerReviewForm.volunteerStatus || "pending",
              })
            }
          >
            {volunteerReviewForm?.volunteerStatus === "approved"
              ? "Approve and Save"
              : volunteerReviewForm?.volunteerStatus === "declined"
                ? "Decline and Save"
                : "Save as Pending"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal for Resources */}
      <Modal
        show={!!editingResource}
        onHide={() => {
          setEditingResource(null);
          setIsCreatingResource(false);
        }}
        size="xl"
        dialogClassName="resource-modal-wide"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{isCreatingResource ? "Add Resource" : "Edit Resource"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingResource && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  name="name"
                  value={editingResource.name || ""}
                  onChange={handleResourceChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  name="phone"
                  value={editingResource.phone || ""}
                  onChange={handleResourceChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  name="website"
                  value={editingResource.website || ""}
                  onChange={handleResourceChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  name="address"
                  value={editingResource.address || ""}
                  onChange={handleResourceChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={editingResource.description || ""}
                  onChange={handleResourceChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Cost Scale (0-5)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={5}
                  name="costScale"
                  value={editingResource.costScale ?? 0}
                  onChange={handleResourceChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={editingResource.status || "approved"}
                  onChange={handleResourceChange}
                >
                  <option value="approved">approved</option>
                  <option value="pending">pending</option>
                  <option value="rejected">rejected</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Zipcode</Form.Label>
                <Form.Control
                  name="zipcode"
                  value={editingResource.zipcode || ""}
                  onChange={handleResourceChange}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Categories</Form.Label>
                <div className="d-flex flex-column gap-2">
                  {majorCategories.map((major) => {
                    const selectedIds = normalizeCategoryIds(editingResource.categoryIds || []);
                    const selectedMajor = selectedIds.includes(Number(major.id));
                    const children = childCategoriesByParent.get(Number(major.id)) || [];
                    return (
                      <div key={`resource-major-${major.id}`} className="border rounded p-2">
                        <Form.Check
                          id={`resource-major-check-${major.id}`}
                          type="checkbox"
                          className="mb-1"
                          checked={selectedMajor}
                          onChange={() =>
                            setEditingResource((prev) => ({
                              ...prev,
                              categoryIds: toggleCategoryIdsWithHierarchy(prev.categoryIds || [], major.id),
                            }))
                          }
                          label={<span className="fw-semibold">{major.name}</span>}
                        />
                        {children.length > 0 && (
                          <div className="d-flex flex-column gap-1 ms-3 mt-1">
                            {children.map((child) => {
                              const selectedChild = selectedIds.includes(Number(child.id));
                              return (
                                <Form.Check
                                  key={`resource-child-btn-${child.id}`}
                                  id={`resource-child-check-${child.id}`}
                                  type="checkbox"
                                  checked={selectedChild}
                                  onChange={() =>
                                    setEditingResource((prev) => ({
                                      ...prev,
                                      categoryIds: toggleCategoryIdsWithHierarchy(prev.categoryIds || [], child.id),
                                    }))
                                  }
                                  label={child.name}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setEditingResource(null);
              setIsCreatingResource(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveResourceChanges}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Category Tree Management Modal */}
      <Modal
        show={showCategoryManagerModal}
        onHide={() => setShowCategoryManagerModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Categories and Subcategories</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Add New Major Category</Form.Label>
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              placeholder="New major category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
            <Button
              type="button"
              variant="outline-success"
              onClick={() => handleCreateCategory(null)}
            >
              Add Major
            </Button>
          </div>

          <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
            {majorCategories.map((major) => {
              const children = childCategoriesByParent.get(Number(major.id)) || [];
              return (
                <li key={`category-tree-modal-${major.id}`} className="border rounded p-3">
                  <div className="d-flex gap-2 flex-wrap align-items-center mb-2">
                    {editingCategoryId === major.id ? (
                      <Form.Control
                        value={categoryDrafts[major.id] ?? major.name}
                        onChange={(e) =>
                          setCategoryDrafts((prev) => ({
                            ...prev,
                            [major.id]: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <div className="fw-semibold">{major.name}</div>
                    )}
                    <Button type="button" variant="outline-info" size="sm" onClick={() => setEditingCategoryId(major.id)}>
                      Edit
                    </Button>
                    {editingCategoryId === major.id && (
                      <Button type="button" variant="outline-success" size="sm" onClick={() => handleRenameCategory(major.id)}>
                        Save
                      </Button>
                    )}
                    <Button type="button" variant="outline-danger" size="sm" onClick={() => handleDeleteCategory(major.id)}>
                      Remove
                    </Button>
                  </div>

                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      placeholder={`New subcategory for ${major.name}`}
                      value={newSubcategoryNames[major.id] || ""}
                      onChange={(e) =>
                        setNewSubcategoryNames((prev) => ({
                          ...prev,
                          [major.id]: e.target.value,
                        }))
                      }
                    />
                    <Button type="button" variant="outline-success" onClick={() => handleCreateCategory(major.id)}>
                      Add Subcategory
                    </Button>
                  </div>

                  {children.length === 0 ? (
                    <div className="small text-muted-soft">No subcategories yet.</div>
                  ) : (
                    <ul className="list-unstyled mb-0 ps-3 d-flex flex-column gap-2">
                      {children.map((child) => (
                        <li key={`category-child-manage-modal-${child.id}`} className="d-flex gap-2 flex-wrap align-items-center">
                          {editingCategoryId === child.id ? (
                            <Form.Control
                              value={categoryDrafts[child.id] ?? child.name}
                              onChange={(e) =>
                                setCategoryDrafts((prev) => ({
                                  ...prev,
                                  [child.id]: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            <div>{child.name}</div>
                          )}
                          <Button type="button" variant="outline-info" size="sm" onClick={() => setEditingCategoryId(child.id)}>
                            Edit
                          </Button>
                          {editingCategoryId === child.id && (
                            <Button type="button" variant="outline-success" size="sm" onClick={() => handleRenameCategory(child.id)}>
                              Save
                            </Button>
                          )}
                          <Button type="button" variant="outline-danger" size="sm" onClick={() => handleDeleteCategory(child.id)}>
                            Remove
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCategoryManagerModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Event Modal */}
      <Modal
        show={showEventModal}
        onHide={() => closeEditEventModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingEvent && (
            <Form onSubmit={handleSaveEditedEvent}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={eventForm.title}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, title: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Date & Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={eventForm.startDate}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, startDate: e.target.value }))}
                />
              </Form.Group>
              <Form.Check
                type="switch"
                id="editMultiDayEvent"
                label="Multi-day event"
                checked={Boolean(eventForm.isMultiDay)}
                onChange={(e) =>
                  setEventForm((prev) => ({
                    ...prev,
                    isMultiDay: e.target.checked,
                    endDate: e.target.checked ? prev.endDate : "",
                  }))
                }
                className="mb-3"
              />
              {eventForm.isMultiDay && (
                <Form.Group className="mb-3">
                  <Form.Label>End Date & Time</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={eventForm.endDate || ""}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Event Address</Form.Label>
                <Form.Control
                  value={eventForm.eventAddress || ""}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, eventAddress: e.target.value }))}
                  placeholder="Street, City, State ZIP"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={eventForm.details}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, details: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Event Images</Form.Label>
                {(eventForm.imageUrls || []).length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {(eventForm.imageUrls || []).map((url, index) => (
                      <div
                        key={`edit-event-image-${index}`}
                        className="position-relative"
                      >
                        <img
                          src={url}
                          alt={`Event preview ${index + 1}`}
                          style={{ width: "84px", height: "84px", objectFit: "cover", borderRadius: "4px" }}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          className="position-absolute top-0 end-0 p-0"
                          style={{ width: "20px", height: "20px", lineHeight: "1" }}
                          onClick={() => removeEventImageAt(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Event Video (optional, max 50MB)</Form.Label>
                {eventForm.videoUrl && <video src={eventForm.videoUrl} className="event-gallery-main mb-2" controls muted />}
                <Form.Control type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime" onChange={handleVideoUpload} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Needed Volunteers</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={eventForm.neededVolunteers}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, neededVolunteers: Number(e.target.value || 0) }))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Approval Status</Form.Label>
                <Form.Select value={eventForm.approvalStatus || "approved"} onChange={(e) => setEventForm((prev) => ({ ...prev, approvalStatus: e.target.value }))}>
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="declined">declined</option>
                </Form.Select>
              </Form.Group>
              <Form.Check
                type="switch"
                id="editPublicEvent"
                label="Public"
                checked={eventForm.isPublic}
                onChange={(e) => setEventForm((prev) => ({ ...prev, isPublic: e.target.checked }))}
                className="mb-2"
              />
              <Form.Check
                type="switch"
                id="editSpotlightEvent"
                label="Spotlight"
                checked={Boolean(eventForm.spotlight)}
                onChange={(e) => setEventForm((prev) => ({ ...prev, spotlight: e.target.checked }))}
                className="mb-3"
              />
              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-grow-1"
                >
                  Save Changes
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => closeEditEventModal()}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showCredentialModal}
        onHide={() => setShowCredentialModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Admin Credential</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small mb-0">
            {generatedCredential || "No credential text available."}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-info" onClick={copyCredentialText} disabled={!generatedCredential}>
            Copy
          </Button>
          <Button variant="secondary" onClick={() => setShowCredentialModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default AdminDashboardPage;










